/**
 * Client HTTP du bureau.
 *
 * Un seul endroit connaît l'adresse de l'API et la façon d'y attacher le jeton.
 * Les écrans appellent des fonctions typées, jamais `fetch` directement.
 *
 * Le jeton d'accès vit **en mémoire** : le stocker dans `localStorage`
 * l'exposerait à toute injection de script. Le rafraîchissement, lui, est
 * persisté — c'est un compromis assumé pour ne pas redemander la double
 * authentification à chaque rechargement de page.
 */

import { CODE_DOUBLON, requeteDoublons, type Doublon, type NiveauDoublon } from '../domaine/doublons';
import { permissionsDuJeton } from '../domaine/droits';

const BASE_URL: string = import.meta.env['VITE_API_BASE_URL'] ?? 'http://localhost:3010/api/v1';

/** Clé du jeton de rafraîchissement, seule valeur persistée. */
const REFRESH_KEY = 'air.refresh';

let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

// Les fiches « doublon » et leur code vivent dans le domaine (logique pure, testée) ; le client les re-expose.
export type { Doublon, NiveauDoublon };

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: string[] | undefined,
    /** `DOUBLON_PROBABLE` quand le serveur a repéré une fiche existante. */
    readonly code?: string | undefined,
    /** Les fiches existantes, quand `code` vaut `DOUBLON_PROBABLE`. */
    readonly doublons?: readonly Doublon[] | undefined,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * Permissions portées par le jeton d'accès actuel — pour AFFICHER ou non un bouton, jamais pour
 * protéger quoi que ce soit : l'API reste l'unique autorité (voir `domaine/droits.ts`). Relu à
 * chaque appel : le jeton change à chaque rafraîchissement, et le décodage ne coûte presque rien.
 */
export function droitsCourants(): ReadonlySet<string> {
  return permissionsDuJeton(accessToken);
}

export function getRefreshToken(): string | null {
  try {
    return window.localStorage.getItem(REFRESH_KEY);
  } catch {
    // Navigation privée, stockage bloqué : on dégrade sans casser la session
    // en cours, seule la reprise après rechargement sera perdue.
    return null;
  }
}

export function setRefreshToken(token: string | null): void {
  try {
    if (token) {
      window.localStorage.setItem(REFRESH_KEY, token);
    } else {
      window.localStorage.removeItem(REFRESH_KEY);
    }
  } catch {
    /* stockage indisponible : sans effet, la session reste valide en mémoire */
  }
}

/** Appelé quand l'API refuse définitivement : l'application doit revenir au login. */
export function onSessionLost(handler: () => void): void {
  onUnauthenticated = handler;
}

interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  /** Désactive la tentative de rafraîchissement — utilisé par le login lui-même. */
  readonly anonymous?: boolean;
  /**
   * Corps BINAIRE brut, envoyé tel quel.
   *
   * Il exclut `body` : l'API attend les octets du fichier, pas du JSON. Le
   * `Content-Type` doit rester binaire, sinon l'analyseur de formulaires
   * d'Express consomme le flux avant le contrôleur et la requête échoue.
   */
  readonly binaire?: Blob;
  /** En-têtes supplémentaires — le nom d'origine du fichier, notamment. */
  readonly entetes?: Readonly<Record<string, string>>;
  /**
   * Transmet et reçoit les cookies de l'API (appareil de confiance MFA).
   * Réservé aux appels d'authentification : le reste du client n'a jamais eu
   * besoin de cookies, inutile d'en élargir la portée sans raison.
   */
  readonly avecCookies?: boolean;
}

async function parseError(response: Response): Promise<ApiError> {
  let message = `Erreur ${response.status}`;
  let details: string[] | undefined;
  let code: string | undefined;
  let doublons: Doublon[] | undefined;

  try {
    const payload = (await response.json()) as {
      message?: string | string[];
      code?: string;
      doublons?: unknown;
    };
    if (Array.isArray(payload.message)) {
      // class-validator renvoie un tableau de messages de champ.
      details = payload.message;
      message = payload.message[0] ?? message;
    } else if (payload.message) {
      message = payload.message;
    }
    // Un doublon probable n'est pas une panne : le corps porte les fiches existantes, l'écran les montre.
    if (payload.code === CODE_DOUBLON && Array.isArray(payload.doublons)) {
      code = payload.code;
      doublons = payload.doublons as Doublon[];
    }
  } catch {
    /* réponse non-JSON : on garde le message générique */
  }

  return new ApiError(response.status, message, details, code, doublons);
}

async function send<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': options.binaire ? 'application/octet-stream' : 'application/json',
    ...options.entetes,
  };
  if (accessToken && !options.anonymous) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const corps = options.binaire ?? (options.body === undefined ? undefined : JSON.stringify(options.body));

  const response = await fetch(`${BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    ...(options.avecCookies ? { credentials: 'include' } : {}),
    ...(corps === undefined ? {} : { body: corps }),
  });

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  return (await response.json()) as T;
}

/**
 * Envoie une requête, et retente UNE fois après rafraîchissement si le jeton a
 * expiré.
 *
 * Une seule tentative : si le rafraîchissement échoue aussi, la session est
 * perdue et l'application doit repasser par le login. Boucler serait pire que
 * de redemander une connexion.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  try {
    return await send<T>(path, options);
  } catch (error) {
    const expired = error instanceof ApiError && error.status === 401 && !options.anonymous;
    if (!expired) {
      throw error;
    }

    const refreshed = await tryRefresh();
    if (!refreshed) {
      onUnauthenticated?.();
      throw error;
    }
    return send<T>(path, options);
  }
}

/**
 * Rotation en cours, partagée par tous les appelants.
 *
 * Le serveur révoque **toute la famille** de jetons dès qu'un jeton de
 * rafraîchissement est présenté deux fois : c'est la détection de rejeu, et
 * elle est voulue. Deux rafraîchissements concurrents présenteraient donc le
 * même jeton et déconnecteraient l'utilisateur — ce qui arrive dès que deux
 * requêtes expirent ensemble, ou que React monte deux fois un effet.
 *
 * Le premier appelant lance la rotation ; les suivants attendent son résultat.
 */
let rotationEnCours: Promise<boolean> | null = null;

function tryRefresh(): Promise<boolean> {
  if (!rotationEnCours) {
    rotationEnCours = executerRotation().finally(() => {
      rotationEnCours = null;
    });
  }
  return rotationEnCours;
}

async function executerRotation(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  try {
    const result = await send<AuthenticatedResult>('/auth/refresh', {
      method: 'POST',
      body: { refreshToken },
      anonymous: true,
    });
    setAccessToken(result.accessToken);
    setRefreshToken(result.refreshToken);
    return true;
  } catch {
    // Jeton révoqué ou famille invalidée : on efface, la session est finie.
    setAccessToken(null);
    setRefreshToken(null);
    return false;
  }
}

// --- Types de l'API ---------------------------------------------------------

export interface MfaRequiredResult {
  readonly status: 'MFA_REQUIRED';
  readonly mfaChallenge: string;
}

export interface MfaSetupRequiredResult {
  readonly status: 'MFA_SETUP_REQUIRED';
  readonly mfaChallenge: string;
  readonly qrDataUri: string | null;
  readonly secretSaisieManuelle: string;
}

export interface AuthenticatedResult {
  readonly status: 'AUTHENTICATED';
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresIn: number;
}

export type LoginResult = MfaRequiredResult | MfaSetupRequiredResult | AuthenticatedResult;

export interface OnboardingStep {
  readonly code: string;
  readonly label: string;
  readonly position: number;
  readonly status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'SKIPPED';
  readonly validatedAt: string | null;
}

export interface Onboarding {
  readonly id: string;
  readonly status: string;
  readonly progress: { readonly done: number; readonly total: number };
  readonly steps: readonly OnboardingStep[];
}

export interface Device {
  readonly id: string;
  readonly label: string;
  readonly fingerprint: string;
  readonly status: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'REVOKED';
  readonly platform: string | null;
  readonly appVersion: string | null;
  readonly lastSeenAt: string | null;
  readonly approvedAt: string | null;
  readonly revokedAt: string | null;
  readonly revokedReason: string | null;
  readonly wipedAt: string | null;
}

// --- Appels ------------------------------------------------------------------

export const api = {
  login: (email: string, password: string) =>
    request<LoginResult>('/auth/login', {
      method: 'POST',
      body: { email, password, surface: 'desk' },
      anonymous: true,
      // Transmet l'éventuel cookie d'appareil de confiance : c'est le seul
      // moyen pour le serveur de dispenser la MFA, jamais une décision prise
      // ici.
      avecCookies: true,
    }),

  verifyMfa: (mfaChallenge: string, code: string, seSouvenirAppareil: boolean) =>
    request<LoginResult>('/auth/mfa/verify', {
      method: 'POST',
      body: { mfaChallenge, code, seSouvenirAppareil },
      anonymous: true,
      avecCookies: true,
    }),

  confirmMfaSetup: (mfaChallenge: string, code: string, seSouvenirAppareil: boolean) =>
    request<LoginResult>('/auth/mfa/setup/confirm', {
      method: 'POST',
      body: { mfaChallenge, code, seSouvenirAppareil },
      anonymous: true,
      avecCookies: true,
    }),

  logout: (refreshToken: string) =>
    request<void>('/auth/logout', { method: 'POST', body: { refreshToken }, anonymous: true }),

  onboarding: () => request<Onboarding>('/onboarding'),

  completeStep: (code: string, skip: boolean) =>
    request<{ code: string; status: string; remaining: number }>(
      `/onboarding/steps/${encodeURIComponent(code)}`,
      { method: 'POST', body: { skip } },
    ),

  devices: (status?: Device['status']) =>
    request<Device[]>(`/devices${status ? `?status=${status}` : ''}`),

  approveDevice: (id: string) => request<Device>(`/devices/${id}/approve`, { method: 'POST' }),

  revokeDevice: (id: string, reason: string) =>
    request<Device>(`/devices/${id}/revoke`, { method: 'POST', body: { reason } }),

  suspendDevice: (id: string, reason: string) =>
    request<Device>(`/devices/${id}/suspend`, { method: 'POST', body: { reason } }),

  parties: (search?: string, role?: PartyRoleKind) => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (role) p.set('role', role);
    const q = p.toString();
    return request<Party[]>(`/parties${q ? `?${q}` : ''}`);
  },

  simulateImport: (fileName: string, content: string) =>
    request<ImportReport>('/imports/parties/simulate', {
      method: 'POST',
      body: { fileName, content },
    }),

  commitImport: (jobId: string) =>
    request<ImportReport>(`/imports/parties/${jobId}/commit`, { method: 'POST' }),

  // --- Sites -----------------------------------------------------------------

  sites: (search?: string, customerId?: string) => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (customerId) p.set('customerId', customerId);
    const q = p.toString();
    return request<Site[]>(`/sites${q ? `?${q}` : ''}`);
  },

  site: (id: string) => request<Site>(`/sites/${id}`),

  // --- Machines --------------------------------------------------------------

  assets: (filtre: {
    search?: string | undefined;
    siteId?: string | undefined;
    /** Toutes les machines d'un client, tous sites confondus. */
    customerId?: string | undefined;
    status?: AssetStatus | undefined;
    assetType?: string | undefined;
    /** Chip secondaire du Parc : `ais` (propriétaire tiers), `esp`, `incomplet`. */
    chip?: 'ais' | 'esp' | 'incomplet' | undefined;
    take?: number | undefined;
    skip?: number | undefined;
  } = {}) => {
    const p = new URLSearchParams();
    if (filtre.customerId) p.set('customerId', filtre.customerId);
    if (filtre.search) p.set('search', filtre.search);
    if (filtre.siteId) p.set('siteId', filtre.siteId);
    if (filtre.status) p.set('status', filtre.status);
    if (filtre.assetType) p.set('assetType', filtre.assetType);
    if (filtre.chip) p.set('chip', filtre.chip);
    if (filtre.take !== undefined) p.set('take', String(filtre.take));
    if (filtre.skip !== undefined) p.set('skip', String(filtre.skip));
    const q = p.toString();
    return request<AssetListResult>(`/assets${q ? `?${q}` : ''}`);
  },

  /** Comptages du parc pour le poste de travail « Le Parc » : combos, statistiques, chips. */
  assetFacets: (
    filtre: {
      customerId?: string | undefined;
      siteId?: string | undefined;
      search?: string | undefined;
    } = {},
  ) => {
    const p = new URLSearchParams();
    if (filtre.customerId) p.set('customerId', filtre.customerId);
    if (filtre.siteId) p.set('siteId', filtre.siteId);
    if (filtre.search) p.set('search', filtre.search);
    const q = p.toString();
    return request<AssetFacets>(`/assets/facets${q ? `?${q}` : ''}`);
  },

  asset: (id: string) => request<Asset>(`/assets/${id}`),

  /**
   * Planche d'étiquettes QR : machines désignées (`ids`) ou filtre du parc.
   * Chaque QR encode le code seul, sans adresse web (arbitrage B13).
   */
  qrLabels: (demande: {
    ids?: readonly string[] | undefined;
    search?: string | undefined;
    siteId?: string | undefined;
    status?: string | undefined;
  }) => {
    const p = new URLSearchParams();
    if (demande.ids && demande.ids.length > 0) p.set('ids', demande.ids.join(','));
    if (demande.search) p.set('search', demande.search);
    if (demande.siteId) p.set('siteId', demande.siteId);
    if (demande.status) p.set('status', demande.status);
    const q = p.toString();
    return request<PlancheQr>(`/assets/qr-labels${q ? `?${q}` : ''}`);
  },

  party: (id: string) => request<Party>(`/parties/${id}`),

  simulateAssetImport: (fileName: string, content: string) =>
    request<ImportReport>('/imports/assets/simulate', {
      method: 'POST',
      body: { fileName, content },
    }),

  commitAssetImport: (jobId: string) =>
    request<ImportReport>(`/imports/assets/${jobId}/commit`, { method: 'POST' }),

  simulateSiteImport: (fileName: string, content: string) =>
    request<ImportReport>('/imports/sites/simulate', {
      method: 'POST',
      body: { fileName, content },
    }),

  commitSiteImport: (jobId: string) =>
    request<ImportReport>(`/imports/sites/${jobId}/commit`, { method: 'POST' }),

  // --- Écritures : tiers, sites, machines ------------------------------------

  createParty: (corps: Record<string, unknown> & { legalName: string }) =>
    request<Party>('/parties', { method: 'POST', body: corps }),

  updateParty: (id: string, corps: Record<string, unknown>) =>
    request<Party>(`/parties/${id}`, { method: 'PATCH', body: corps }),

  archiveParty: (id: string, reason?: string) =>
    request<void>(`/parties/${id}/archive`, { method: 'POST', body: { reason } }),

  createSite: (corps: Record<string, unknown>) =>
    request<Site>('/sites', { method: 'POST', body: corps }),

  updateSite: (id: string, corps: Record<string, unknown>) =>
    request<Site>(`/sites/${id}`, { method: 'PATCH', body: corps }),

  archiveSite: (id: string, reason?: string) =>
    request<void>(`/sites/${id}/archive`, { method: 'POST', body: { reason } }),

  createAsset: (corps: Record<string, unknown>) =>
    request<Asset>('/assets', { method: 'POST', body: corps }),

  updateAsset: (id: string, corps: Record<string, unknown>) =>
    request<Asset>(`/assets/${id}`, { method: 'PATCH', body: corps }),

  archiveAsset: (id: string, reason?: string) =>
    request<void>(`/assets/${id}/archive`, { method: 'POST', body: { reason } }),

  // --- Doublons : les fiches qui ressemblent à ce qu'on s'apprête à saisir ----------
  // Un simple avertissement, avant l'envoi. Le serveur refait le contrôle à l'enregistrement
  // (HTTP 409, code DOUBLON_PROBABLE) : cette lecture n'est qu'un confort.

  doublonsMachine: (critere: {
    serialNumber?: string | undefined;
    brandPartyId?: string | undefined;
    model?: string | undefined;
    siteId?: string | undefined;
    clientDesignation?: string | undefined;
    excludeId?: string | undefined;
  }) => request<{ doublons: Doublon[] }>(`/assets/doublons${requeteDoublons(critere)}`),

  // --- Lot T4 : les machines créées sur le téléphone, à valider par le bureau ---------
  machinesAValider: (search?: string) =>
    request<MachinesAValider>(`/assets/a-valider${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  validerMachine: (id: string, revision: number) =>
    request<{ id: string; reviewStatus: 'VALIDATED'; revision: number }>(`/assets/${id}/validate`, { method: 'POST', body: { revision } }),
  fusionnerMachine: (id: string, cibleId: string, revision: number) =>
    request<FusionMachine>(`/assets/${id}/merge`, { method: 'POST', body: { cibleId, revision } }),

  doublonsTiers: (critere: {
    legalName?: string | undefined;
    legalIdentifier?: string | undefined;
    excludeId?: string | undefined;
  }) => request<{ doublons: Doublon[] }>(`/parties/doublons${requeteDoublons(critere)}`),

  doublonsSite: (critere: {
    customerId: string;
    name?: string | undefined;
    addressLine1?: string | undefined;
    city?: string | undefined;
    excludeId?: string | undefined;
  }) => request<{ doublons: Doublon[] }>(`/sites/doublons${requeteDoublons(critere)}`),

  // --- Champs techniques configurables ---------------------------------------

  attributeDefinitions: (assetType?: string) =>
    request<AttributeDefinition[]>(
      `/assets/attribute-definitions${assetType ? `?assetType=${encodeURIComponent(assetType)}` : ''}`,
    ),

  createAttributeDefinition: (corps: Record<string, unknown>) =>
    request<AttributeDefinition>('/assets/attribute-definitions', { method: 'POST', body: corps }),

  seedAttributeDefinitions: () =>
    request<{ created: number; skipped: number }>('/assets/attribute-definitions/seed', {
      method: 'POST',
    }),

  /** `appliesToAssetType: null` = s'applique à toutes les familles de machines. */
  updateAttributeDefinition: (id: string, corps: Record<string, unknown>) =>
    request<AttributeDefinition>(`/assets/attribute-definitions/${id}`, {
      method: 'PATCH',
      body: corps,
    }),

  // --- Paramètres de l'espace ------------------------------------------------

  settings: () => request<Settings>('/settings'),
  identiteEspace: () => request<IdentiteEspace>('/settings/identite'),

  updateSettings: (corps: Record<string, unknown>) =>
    request<Settings>('/settings', { method: 'PATCH', body: corps }),

  // --- Membres de l'espace ---------------------------------------------------

  members: () => request<Membre[]>('/users'),

  roles: () => request<RoleEspace[]>('/roles'),

  invite: (corps: {
    email: string;
    firstName?: string;
    lastName?: string;
    jobTitle?: string;
    roleIds: string[];
  }) => request<InvitationCreee>('/users/invite', { method: 'POST', body: corps }),

  updateMember: (membershipId: string, corps: Record<string, unknown>) =>
    request<Membre>(`/users/${membershipId}`, { method: 'PATCH', body: corps }),

  revokeMember: (membershipId: string, reason?: string) =>
    request<void>(`/users/${membershipId}/revoke`, { method: 'POST', body: { reason } }),

  resetMemberMfa: (membershipId: string) =>
    request<void>(`/users/${membershipId}/mfa/reset`, { method: 'POST' }),

  // --- Acceptation d'invitation : PUBLIQUE, sans jeton d'accès ---------------

  readInvitation: (token: string) =>
    request<Invitation>(`/invitations/${encodeURIComponent(token)}`, { anonymous: true }),

  acceptInvitation: (token: string, password: string) =>
    request<{
      email: string;
      mfaSecret: string | null;
      mfaUri: string | null;
      /** Image PNG en `data:` produite par le serveur. `null` si le rendu a échoué. */
      mfaQrDataUri: string | null;
    }>(
      `/invitations/${encodeURIComponent(token)}/accept`,
      { method: 'POST', body: { password }, anonymous: true },
    ),

  // --- Pièces jointes : photos et documents ---------------------------------

  /**
   * Version EN VIGUEUR de chaque document d'un objet.
   *
   * `categoryId` vaut `'aucune'` pour les documents non classés.
   */
  files: (
    ownerType: OwnerType,
    ownerId: string,
    filtre: { categoryId?: string | undefined; search?: string | undefined } = {},
  ) => {
    const p = new URLSearchParams({ ownerType, ownerId });
    if (filtre.categoryId) p.set('categoryId', filtre.categoryId);
    if (filtre.search) p.set('search', filtre.search);
    return request<PieceJointe[]>(`/files?${p.toString()}`);
  },

  /**
   * Dépose UN fichier, ou la nouvelle version d'un document (`replacesId`).
   *
   * Le nom d'origine voyage encodé dans un en-tête : un en-tête HTTP ne
   * transporte que de l'ASCII, et « Vue d'ensemble été.jpg » n'en est pas.
   */
  uploadFile: (
    ownerType: OwnerType,
    ownerId: string,
    fichier: File,
    options: { categoryId?: string | undefined; replacesId?: string | undefined } = {},
  ) => {
    const p = new URLSearchParams({ ownerType, ownerId });
    if (options.categoryId) p.set('categoryId', options.categoryId);
    if (options.replacesId) p.set('replacesId', options.replacesId);
    return request<PieceJointe>(`/files?${p.toString()}`, {
      method: 'POST',
      binaire: fichier,
      entetes: { 'X-Nom-Fichier': encodeURIComponent(fichier.name) },
    });
  },

  fileUrl: (id: string) =>
    request<{ url: string; expireDans: number; contentType: string }>(`/files/${id}/url`),

  /** Retire le document ENTIER : toutes ses versions. */
  archiveFile: (id: string) => request<void>(`/files/${id}`, { method: 'DELETE' }),

  /** Toutes les versions d'un document, la plus récente d'abord. */
  fileVersions: (id: string) => request<PieceJointe[]>(`/files/${id}/versions`),

  /** Classe un document — toutes ses versions. `null` retire le classement. */
  classifyFile: (id: string, categoryId: string | null) =>
    request<PieceJointe>(`/files/${id}`, { method: 'PATCH', body: { categoryId } }),

  /** Renomme un document — toutes ses versions. `null` retire le nom choisi. */
  renameFile: (id: string, label: string | null) =>
    request<PieceJointe>(`/files/${id}/rename`, { method: 'PATCH', body: { label } }),

  documentCategories: (ownerType?: OwnerType) =>
    request<CategorieDocument[]>(
      `/document-categories${ownerType ? `?ownerType=${ownerType}` : ''}`,
    ),

  createDocumentCategory: (corps: { label: string; appliesTo: OwnerType[] }) =>
    request<CategorieDocument>('/document-categories', { method: 'POST', body: corps }),

  seedDocumentCategories: () =>
    request<{ created: number; skipped: number }>('/document-categories/seed', {
      method: 'POST',
    }),

  archiveDocumentCategory: (id: string) =>
    request<void>(`/document-categories/${id}`, { method: 'DELETE' }),

  // --- Établissements et contacts -------------------------------------------

  establishments: (partyId: string) =>
    request<Etablissement[]>(`/parties/${partyId}/establishments`),

  createEstablishment: (partyId: string, corps: Record<string, unknown>) =>
    request<Etablissement>(`/parties/${partyId}/establishments`, { method: 'POST', body: corps }),

  updateEstablishment: (id: string, corps: Record<string, unknown>) =>
    request<Etablissement>(`/establishments/${id}`, { method: 'PATCH', body: corps }),

  archiveEstablishment: (id: string) =>
    request<void>(`/establishments/${id}`, { method: 'DELETE' }),

  contacts: (partyId: string) => request<Contact[]>(`/parties/${partyId}/contacts`),

  createContact: (partyId: string, corps: Record<string, unknown>) =>
    request<Contact>(`/parties/${partyId}/contacts`, { method: 'POST', body: corps }),

  updateContact: (id: string, corps: Record<string, unknown>) =>
    request<Contact>(`/contacts/${id}`, { method: 'PATCH', body: corps }),

  archiveContact: (id: string) => request<void>(`/contacts/${id}`, { method: 'DELETE' }),

  addChannel: (contactId: string, corps: Record<string, unknown>) =>
    request<Contact>(`/contacts/${contactId}/channels`, { method: 'POST', body: corps }),

  updateChannel: (id: string, corps: Record<string, unknown>) =>
    request<Contact>(`/channels/${id}`, { method: 'PATCH', body: corps }),

  archiveChannel: (id: string) => request<Contact>(`/channels/${id}`, { method: 'DELETE' }),

  // --- Compteurs et relevés -------------------------------------------------

  counters: (assetId: string) => request<Compteur[]>(`/assets/${assetId}/counters`),

  counterLabels: () => request<string[]>('/counters/labels'),

  createCounter: (
    assetId: string,
    corps: { label: string; unit?: string; monotonic?: boolean; drivesMaintenance?: boolean },
  ) => request<Compteur>(`/assets/${assetId}/counters`, { method: 'POST', body: corps }),

  archiveCounter: (counterId: string) =>
    request<void>(`/counters/${counterId}`, { method: 'DELETE' }),

  /** Désigne (ou retire) LE compteur qui commande les entretiens de la machine : un seul par machine. */
  designateCounter: (counterId: string, drivesMaintenance: boolean) =>
    request<Compteur>(`/counters/${counterId}`, { method: 'PATCH', body: { drivesMaintenance } }),

  readings: (counterId: string) => request<Releve[]>(`/counters/${counterId}/readings`),

  /**
   * Enregistre un relevé.
   *
   * `value` est une CHAÎNE : un index de compteur dépasse vite la précision
   * exacte d'un nombre JavaScript, et JSON n'a qu'un seul type numérique.
   */
  recordReading: (
    counterId: string,
    corps: { value: string; readAt?: string; note?: string; correctsReadingId?: string },
  ) => request<Releve>(`/counters/${counterId}/readings`, { method: 'POST', body: corps }),

  setAssetAttribute: (
    assetId: string,
    definitionId: string,
    saisie: { completeness: Completeness; value?: string; unavailableReason?: string },
  ) =>
    request<Asset>(`/assets/${assetId}/attributes/${definitionId}`, {
      method: 'PUT',
      body: saisie,
    }),

  // --- Conformité réglementaire (CDC §6.4) -----------------------------------

  complianceRules: (archived?: boolean) =>
    request<ComplianceRule[]>(
      `/compliance/rules${archived !== undefined ? `?archived=${archived}` : ''}`,
    ),

  complianceRule: (id: string) => request<ComplianceRule>(`/compliance/rules/${id}`),

  createComplianceRule: (corps: {
    code?: string;
    label: string;
    jurisdiction: string;
    ruleType: string;
  }) => request<ComplianceRule>('/compliance/rules', { method: 'POST', body: corps }),

  archiveComplianceRule: (id: string) =>
    request<void>(`/compliance/rules/${id}/archive`, { method: 'POST' }),

  createComplianceRuleVersion: (
    ruleId: string,
    corps: {
      validFrom: string;
      validTo?: string;
      source: string;
      motif: string;
      formula: ComplianceFormula;
    },
  ) =>
    request<ComplianceRuleVersion>(`/compliance/rules/${ruleId}/versions`, {
      method: 'POST',
      body: corps,
    }),

  /** Rend la version IMMUABLE : plus aucune correction n'est possible ensuite. */
  publishComplianceRuleVersion: (ruleId: string, versionId: string) =>
    request<ComplianceRuleVersion>(`/compliance/rules/${ruleId}/versions/${versionId}/publish`, {
      method: 'POST',
    }),

  assetComplianceRules: (assetId: string) =>
    request<ComplianceRuleAssignment[]>(`/assets/${assetId}/compliance-rules`),

  assignComplianceRule: (assetId: string, ruleId: string) =>
    request<ComplianceRuleAssignment>(`/assets/${assetId}/compliance-rules/${ruleId}`, {
      method: 'POST',
    }),

  unassignComplianceRule: (assetId: string, ruleId: string) =>
    request<void>(`/assets/${assetId}/compliance-rules/${ruleId}`, { method: 'DELETE' }),

  assetComplianceObligations: (assetId: string) =>
    request<ComplianceObligation[]>(`/assets/${assetId}/compliance-obligations`),

  completeComplianceObligation: (
    id: string,
    corps: {
      completedAt: string;
      outcome: ComplianceOutcome;
      note?: string;
      organismePartyId?: string;
    },
  ) =>
    request<ComplianceObligation>(`/compliance/obligations/${id}/complete`, {
      method: 'POST',
      body: corps,
    }),

  // --- Gammes d'entretien (CDC §6.3, §8.3) -----------------------------------

  maintenanceProfiles: (archived?: boolean) =>
    request<GammeResume[]>(`/maintenance/profiles${archived ? '?archived=true' : ''}`),

  maintenanceProfile: (id: string) => request<GammeDetail>(`/maintenance/profiles/${id}`),

  createMaintenanceProfile: (corps: CreerGammeInput) =>
    request<GammeResume>('/maintenance/profiles', { method: 'POST', body: corps }),

  updateMaintenanceProfile: (id: string, corps: ModifierGammeInput) =>
    request<GammeResume>(`/maintenance/profiles/${id}`, { method: 'PATCH', body: corps }),

  archiveMaintenanceProfile: (id: string) =>
    request<void>(`/maintenance/profiles/${id}/archive`, { method: 'POST' }),

  unarchiveMaintenanceProfile: (id: string) =>
    request<void>(`/maintenance/profiles/${id}/unarchive`, { method: 'POST' }),

  createMaintenanceVersion: (profileId: string, corps: CreerVersionInput) =>
    request<GammeVersion>(`/maintenance/profiles/${profileId}/versions`, { method: 'POST', body: corps }),

  updateMaintenanceVersion: (profileId: string, versionId: string, corps: ModifierVersionInput) =>
    request<GammeVersion>(`/maintenance/profiles/${profileId}/versions/${versionId}`, {
      method: 'PATCH',
      body: corps,
    }),

  /**
   * Remplace d'un bloc le contenu d'un brouillon. `revision` est le jeton de concurrence reçu avec
   * le brouillon : s'il a changé, l'API refuse (409) au lieu d'écraser le travail d'un autre.
   */
  replaceMaintenanceContent: (profileId: string, versionId: string, corps: RemplacerContenuInput) =>
    request<GammeVersion>(`/maintenance/profiles/${profileId}/versions/${versionId}/content`, {
      method: 'PUT',
      body: corps,
    }),

  /** Les équivalents déjà connus d'une référence officielle de la marque de la gamme (aucun prix). */
  maintenanceEquivalents: (profileId: string, reference: string) =>
    request<EquivalentConnu[]>(
      `/maintenance/profiles/${profileId}/equivalents?reference=${encodeURIComponent(reference)}`,
    ),

  /**
   * Les désignations DÉJÀ connues d'une référence officielle de la marque de la gamme : le texte de la
   * pièce, la plus récente d'abord. Aucun prix : ouvert à tout rôle qui lit les gammes.
   */
  maintenanceDesignations: (profileId: string, reference: string) =>
    request<DesignationConnue[]>(
      `/maintenance/profiles/${profileId}/designations?reference=${encodeURIComponent(reference)}`,
    ),

  /**
   * « Je ne connais pas la référence entière » : les références du tarif du fabricant de la gamme qui
   * commencent par ce qui est tapé (8 au plus, avec le nombre total). Ouvert à tout rôle qui lit les
   * gammes ; le prix n'est rendu qu'à qui voit les prix (la clé est sinon absente).
   */
  maintenanceReferencesTarif: (profileId: string, saisie: string) =>
    request<ReferencesDuTarif>(
      `/maintenance/profiles/${profileId}/references-tarif?q=${encodeURIComponent(saisie)}`,
    ),

  /**
   * La même aide, côté FOURNISSEUR (écran des conditions d'achat) : les références de ses tarifs en vigueur qui
   * commencent par ce qui est tapé (8 au plus, avec le total), prix du tarif compris. Réservé à qui voit les prix.
   */
  referencesFournisseur: (partyId: string, saisie: string) =>
    request<ReferencesDuTarif>(
      `/pricing/references?partyId=${encodeURIComponent(partyId)}&q=${encodeURIComponent(saisie)}`,
    ),

  /**
   * Les références du tarif de la MARQUE d'une machine qui commencent par ce qui est tapé : l'aide de la saisie d'un
   * article sur un bon pour une machine qui n'a pas de gamme d'entretien (8 au plus, avec le total). Le prix du tarif
   * n'est rendu qu'à qui voit les prix : la clé est absente sinon.
   */
  referencesTarifMachine: (assetId: string, saisie: string) =>
    request<ReferencesDuTarif>(`/assets/${assetId}/references-tarif?q=${encodeURIComponent(saisie)}`),

  /**
   * Contrôle au tarif de la marque de la machine les références des pièces qu'on vient de verser d'une gamme dans un
   * bon : au tarif (rien à faire), plus au tarif (remplacée toute seule si c'est sûr, sinon à choisir), ou tarif non
   * chargé. Une réponse par ligne, dans l'ordre. Aucun prix. N'écrit rien.
   */
  verifierReferencesBon: (assetId: string, lignes: readonly LigneAVerifierBon[]) =>
    request<ReferencesVerifieesBon>(`/assets/${assetId}/references-tarif/verifier`, { method: 'POST', body: { lignes } }),

  /**
   * Crée sur la machine les compteurs STANDARD (températures, pression, tension…) dont une valeur est saisie dans le bon,
   * et rend l'identifiant de chacun (celui qui existe déjà est réutilisé). N'écrit aucun relevé : le relevé naît à la validation.
   */
  creerCompteursStandard: (assetId: string, corps: { natureId: string; keys: readonly string[] }) =>
    request<{ compteurs: readonly CompteurStandardCree[] }>(`/assets/${assetId}/valeurs-standard`, { method: 'POST', body: corps }),

  /**
   * Les prix DÉJÀ connus d'une référence, lus sur les gammes en vigueur : prix public pour la
   * référence officielle (`OFFICIAL`), prix interne pour un équivalent (`ADAPTABLE`). Réservé à qui
   * voit les prix : un autre reçoit 403, donc l'éditeur ne l'appelle que s'il peut les écrire.
   */
  maintenancePrixConnus: (
    profileId: string,
    reference: string,
    origine: 'OFFICIAL' | 'ADAPTABLE',
    fournisseurId?: string | null,
  ) =>
    request<PrixConnu[]>(
      `/maintenance/profiles/${profileId}/prix-connus?reference=${encodeURIComponent(reference)}` +
        `&origine=${origine}` +
        (fournisseurId ? `&fournisseur=${encodeURIComponent(fournisseurId)}` : ''),
    ),

  // ------------------------------------------------- tarifs des fabricants (lot C1)

  /** Les tarifs des fabricants et leur version en vigueur (aucun prix). */
  tarifs: (archives = false) => request<TarifResume[]>(`/catalogue/price-lists${archives ? '?archived=true' : ''}`),

  tarif: (id: string) => request<TarifDetail>(`/catalogue/price-lists/${id}`),

  creerTarif: (corps: { partyId: string; label: string }) =>
    request<TarifResume>('/catalogue/price-lists', { method: 'POST', body: corps }),

  archiverTarif: (id: string) => request<void>(`/catalogue/price-lists/${id}/archive`, { method: 'POST' }),

  /**
   * Cherche une référence ou une désignation dans la version en vigueur d'un tarif (ou `versionId`).
   * Le prix n'est rendu que pour qui détient le droit de le voir : la clé `prix` est absente sinon.
   */
  rechercherTarif: (id: string, params: { search?: string; take?: number; skip?: number; versionId?: string } = {}) => {
    const requete = new URLSearchParams();
    if (params.search) requete.set('search', params.search);
    if (params.take !== undefined) requete.set('take', String(params.take));
    if (params.skip !== undefined) requete.set('skip', String(params.skip));
    if (params.versionId) requete.set('versionId', params.versionId);
    const texte = requete.toString();
    return request<ResultatRechercheTarif>(`/catalogue/price-lists/${id}/lines${texte ? `?${texte}` : ''}`);
  },

  /** Ce que changerait ce fichier de tarif, SANS RIEN ÉCRIRE. Réservé à qui gère et voit les prix. */
  apercuTarif: (id: string, fichier: File, feuille?: string) =>
    request<ApercuImportTarif>(
      `/catalogue/price-lists/${id}/versions/preview${feuille ? `?feuille=${encodeURIComponent(feuille)}` : ''}`,
      { method: 'POST', binaire: fichier, entetes: { 'X-Nom-Fichier': encodeURIComponent(fichier.name) } },
    ),

  /** Importe le MÊME fichier comme nouvelle version (une transaction, tout ou rien). */
  publierTarif: (
    id: string,
    fichier: File,
    entree: { validFrom: string; sha256: string; source?: string; feuille?: string },
  ) => {
    const requete = new URLSearchParams({ validFrom: entree.validFrom, sha256: entree.sha256 });
    if (entree.source) requete.set('source', entree.source);
    if (entree.feuille) requete.set('feuille', entree.feuille);
    return request<VersionTarif>(`/catalogue/price-lists/${id}/versions?${requete.toString()}`, {
      method: 'POST',
      binaire: fichier,
      entetes: { 'X-Nom-Fichier': encodeURIComponent(fichier.name) },
    });
  },

  archiverVersionTarif: (id: string, versionId: string) =>
    request<void>(`/catalogue/price-lists/${id}/versions/${versionId}/archive`, { method: 'POST' }),

  // ------------------------------------------------- conditions d'achat (registre B26, lot P1)

  /** Les familles de la version en vigueur d'un tarif (réservé à qui voit les prix). */
  famillesTarif: (listId: string) => request<FamillesDuTarif>(`/catalogue/price-lists/${listId}/familles`),

  /** Les conditions d'achat d'un fournisseur ou d'une marque, avec l'historique (aucune valeur par défaut). */
  conditionsAchat: (partyId: string, archivees = false) =>
    request<ConditionsDuTiers>(`/pricing/conditions?partyId=${encodeURIComponent(partyId)}${archivees ? '&archived=true' : ''}`),

  /** Définit une condition, DATÉE : elle ne modifie jamais une précédente, elle s'y ajoute. */
  definirConditionAchat: (corps: EntreeConditionAchat) =>
    request<CreationConditionAchat>('/pricing/conditions', { method: 'POST', body: corps }),

  /** Les fournisseurs qui ont au moins une condition d'achat en cours, avec leur nombre (pour ne lister que ceux-là). */
  fournisseursAvecConditions: () =>
    request<{ partyId: string; nbConditions: number }[]>('/pricing/fournisseurs'),

  /** « Cette ligne n'a jamais existé » : correction d'une faute de saisie, définitive. */
  archiverConditionAchat: (id: string) => request<void>(`/pricing/conditions/${id}/archive`, { method: 'POST' }),

  /** Essaie une référence : prix du tarif, famille, condition retenue, prix d'achat et de vente. N'écrit rien. */
  essaiPrix: (params: { partyId: string; reference: string; date?: string; prix?: string }) => {
    const requete = new URLSearchParams({ partyId: params.partyId, reference: params.reference });
    if (params.date) requete.set('date', params.date);
    if (params.prix) requete.set('prix', params.prix);
    return request<ResultatEssaiPrix>(`/pricing/essai?${requete.toString()}`);
  },

  publishMaintenanceVersion: (profileId: string, versionId: string) =>
    request<GammeVersion>(`/maintenance/profiles/${profileId}/versions/${versionId}/publish`, {
      method: 'POST',
    }),

  /**
   * Lot C2 — la REVUE : les lignes des gammes du fabricant comparées à une version de tarif, par référence.
   * Lecture seule. Le prix n'est rendu qu'à qui voit les prix (clés absentes sinon).
   */
  revueTarifGammes: (listId: string, versionId: string) =>
    request<RevueTarifGammes>(`/maintenance/tarif-gammes/${listId}/versions/${versionId}`),

  /**
   * Lot C2 — crée un BROUILLON par gamme concernée, avec les seuls changements décidés. On n'envoie que des
   * décisions par référence : le serveur recalcule tout depuis le tarif et les gammes.
   */
  appliquerTarifGammes: (listId: string, versionId: string, corps: AppliquerTarifGammesInput) =>
    request<BilanApplicationTarif>(`/maintenance/tarif-gammes/${listId}/versions/${versionId}`, {
      method: 'POST',
      body: corps,
    }),

  /**
   * Lot P2 — la REVUE du recalcul des prix d'une marque : ce que les conditions d'achat du jour changeraient aux prix
   * de ses gammes, et ce qui manque pour qu'ils se calculent. Lecture seule ; exige de gérer les gammes ET les prix.
   */
  revueRecalculPrix: (partyId: string) => request<RevueRecalculPrix>(`/maintenance/recalcul-prix/${partyId}`),

  /**
   * Lot P2 — crée un BROUILLON par gamme dont les prix changeraient. Le client n'envoie que les gammes (avec la version
   * en vigueur que la revue a montrée) et la date d'effet : le serveur recalcule tout. Publier un brouillon fige les prix.
   */
  appliquerRecalculPrix: (partyId: string, corps: AppliquerRecalculPrixInput) =>
    request<BilanRecalculPrix>(`/maintenance/recalcul-prix/${partyId}`, { method: 'POST', body: corps }),

  /** Archive une version publiée, ou abandonne un brouillon : rien ne se supprime. */
  archiveMaintenanceVersion: (profileId: string, versionId: string) =>
    request<GammeVersion>(`/maintenance/profiles/${profileId}/versions/${versionId}/archive`, {
      method: 'POST',
    }),

  /** Gamme rattachée à une machine, ou gammes suggérées si elle n'en a pas. */
  assetMaintenance: (assetId: string) =>
    request<MaintenanceMachine>(`/assets/${assetId}/maintenance`),

  assignMaintenance: (assetId: string, profileId: string) =>
    request<RattachementGamme>(`/assets/${assetId}/maintenance/profile/${profileId}`, {
      method: 'POST',
    }),

  unassignMaintenance: (assetId: string) =>
    request<void>(`/assets/${assetId}/maintenance/profile`, { method: 'DELETE' }),

  /** Lot B — le prochain entretien d'une machine : calculé à la lecture, ou ce qui manque pour le dire. */
  prochainEntretien: (assetId: string) =>
    request<ProchainEntretien>(`/assets/${assetId}/maintenance/next`),

  reglagesEntretien: (assetId: string) =>
    request<ReglagesEntretien>(`/assets/${assetId}/maintenance/settings`),

  /** Clé absente = inchangé ; `null` = effacer. Le jeton `version` est exigé dès que des réglages existent. */
  modifierReglagesEntretien: (
    assetId: string,
    corps: {
      declaredHoursPerYear?: number | null;
      triggerMode?: ModeDeclenchement | null;
      calendarMonths?: number | null;
      version?: number;
    },
  ) =>
    request<ReglagesEntretien>(`/assets/${assetId}/maintenance/settings`, { method: 'PATCH', body: corps }),

  /** Lot B2 — les entretiens réalisés d'une machine, du plus récent au plus ancien, avec leur état. */
  entretiensRealises: (assetId: string, take?: number) =>
    request<ListeEntretiensRealises>(`/assets/${assetId}/maintenance/services${take ? `?take=${take}` : ''}`),

  /** Enregistre un entretien réalisé, ou en rectifie un (`corrigeId`). */
  enregistrerEntretien: (assetId: string, corps: EntreeEntretienRealise) =>
    request<EnregistrementEntretien>(`/assets/${assetId}/maintenance/services`, { method: 'POST', body: corps }),

  /** Annule un entretien : il reste lisible, marqué « annulé ». Le relevé lié reste (un relevé ne s'annule pas). */
  annulerEntretien: (assetId: string, recordId: string, corps: { note?: string } = {}) =>
    request<EnregistrementEntretien>(`/assets/${assetId}/maintenance/services/${recordId}/void`, {
      method: 'POST',
      body: corps,
    }),

  // --- Bons d'intervention saisis au bureau (lot I1.1) ----------------------------

  /** Les bons, filtrables. `statuses` : « DRAFT,TO_REVIEW ». */
  bons: (filtre: FiltreBons = {}) => {
    const params = new URLSearchParams();
    for (const [cle, valeur] of Object.entries(filtre)) {
      if (valeur !== undefined && valeur !== '') params.set(cle, String(valeur));
    }
    const texte = params.toString();
    return request<ListeBons>(`/interventions${texte === '' ? '' : `?${texte}`}`);
  },

  bon: (id: string) => request<BonDetail>(`/interventions/${id}`),

  /** Les derniers bons d'une machine (la carte de sa fiche). */
  bonsDeLaMachine: (assetId: string, take?: number) =>
    request<ListeBons>(`/assets/${assetId}/interventions${take ? `?take=${take}` : ''}`),

  /** Les noms proposés dans le menu « Technicien » (annuaire des noms et comptes au rôle Technicien). */
  techniciens: () => request<TechnicienBon[]>('/interventions/techniciens'),

  /** Crée le bon : le numéro est alloué ICI, à la première sauvegarde. */
  creerBon: (corps: EntreeCreationBon) => request<BonDetail>('/interventions', { method: 'POST', body: corps }),

  /** Enregistre le brouillon. Un champ absent reste inchangé, `null` le vide ; le contenu REMPLACE le précédent. */
  sauvegarderBon: (id: string, corps: EntreeSauvegardeBon) =>
    request<BonDetail>(`/interventions/${id}`, { method: 'PATCH', body: corps }),

  soumettreBon: (id: string, revision: number) =>
    request<BonDetail>(`/interventions/${id}/submit`, { method: 'POST', body: { revision } }),

  rouvrirBon: (id: string, revision: number) =>
    request<BonDetail>(`/interventions/${id}/reopen`, { method: 'POST', body: { revision } }),

  /** Confirmer une intervention : lui donner son numéro (une occurrence de tournée n'en a pas encore). Rejouable. */
  confirmerIntervention: (id: string, revision: number) =>
    request<BonDetail>(`/interventions/${id}/confirm`, { method: 'POST', body: { revision } }),

  annulerBon: (id: string, revision: number, reason: string) =>
    request<BonDetail>(`/interventions/${id}/cancel`, { method: 'POST', body: { revision, reason } }),

  /** Ce que la validation ÉCRIRA (le serveur joue la vraie validation dans une transaction qu'il annule). */
  apercuValidationBon: (id: string) => request<ApercuValidationBon>(`/interventions/${id}/validation`),

  /** Valide le bon : écrit ses relevés, son entretien, ses lignes et ses suites, tout ou rien. */
  validerBon: (id: string, revision: number, billing?: FacturationBon) =>
    request<BonDetail>(`/interventions/${id}/review`, { method: 'POST', body: billing === undefined ? { revision } : { revision, billing } }),

  /** Les suites à donner d'une machine (ouvertes par défaut ; `statuses` : « OPEN,CLOSED »). */
  suitesDeLaMachine: (assetId: string, statuses?: string) =>
    request<ListeSuitesMachine>(`/assets/${assetId}/follow-ups${statuses ? `?statuses=${statuses}` : ''}`),

  /**
   * La file « Suites à traiter » : les suites de toutes les machines (ouvertes par défaut). `statuses` : « OPEN,CLOSED » ;
   * `kinds` : « QUOTE_TO_DO,PART_TO_ORDER ».
   */
  suitesATraiter: (filtre: FiltreSuitesATraiter = {}) => {
    const params = new URLSearchParams();
    for (const [cle, valeur] of Object.entries(filtre)) {
      if (valeur !== undefined && valeur !== '') params.set(cle, String(valeur));
    }
    const texte = params.toString();
    return request<ListeSuitesATraiter>(`/follow-ups${texte === '' ? '' : `?${texte}`}`);
  },

  /** Écarte une suite (« elle ne sert plus à rien »). Raison obligatoire ; DÉFINITIF. */
  ecarterSuite: (id: string, revision: number, reason: string) =>
    request<SuiteATraiter>(`/follow-ups/${id}/dismiss`, { method: 'POST', body: { revision, reason } }),

  /** Clôt une suite (« c'est fait »). Raison obligatoire ; DÉFINITIF. */
  cloreSuite: (id: string, revision: number, reason: string) =>
    request<SuiteATraiter>(`/follow-ups/${id}/close`, { method: 'POST', body: { revision, reason } }),

  naturesBon: (inclureArchivees = false) =>
    request<NatureBon[]>(`/intervention-natures${inclureArchivees ? '?includeArchived=true' : ''}`),

  /** Installe les natures de départ (rejouable). */
  semerNaturesBon: () => request<BilanSemisNatures>('/intervention-natures/seed', { method: 'POST' }),

  /** Crée une nature de bon (le code se déduit du libellé). */
  creerNatureBon: (corps: {
    label: string;
    layout?: ModeleBon;
    recordsService?: boolean;
    sortOrder?: number;
    checkItems?: readonly ElementControleNature[];
  }) => request<NatureBon>('/intervention-natures', { method: 'POST', body: corps }),

  /** Modifie une nature : libellé, ordre, option « enregistre un entretien », liste COMPLÈTE de contrôles, archivage. */
  modifierNatureBon: (
    id: string,
    corps: {
      revision: number;
      label?: string;
      layout?: ModeleBon;
      recordsService?: boolean;
      sortOrder?: number;
      checkItems?: readonly ElementControleNature[];
      archived?: boolean;
    },
  ) => request<NatureBon>(`/intervention-natures/${id}`, { method: 'PATCH', body: corps }),

  // --- Planning : l'INTERVENTION UNIQUE (ADR-0016) : une case par jour travaillé -----------------------

  /** La semaine, lue depuis les interventions : une case par jour travaillé et par membre de l'équipe. */
  planningSemaineInterventions: (depuis?: string, avecAnnulees = false, vue: 'semaine' | 'mois' = 'semaine') => {
    const p = new URLSearchParams();
    if (depuis) p.set('from', depuis);
    if (avecAnnulees) p.set('annulees', 'true');
    if (vue === 'mois') p.set('vue', 'mois');
    const q = p.toString();
    return request<SemaineInterventions>(`/planning/interventions/week${q ? `?${q}` : ''}`);
  },

  /** Les interventions ouvertes SANS aucun jour : la fenêtre qui finit le plus tôt d'abord. */
  planningAAffecterInterventions: (take?: number, skip?: number) => {
    const p = new URLSearchParams();
    if (take !== undefined) p.set('take', String(take));
    if (skip !== undefined) p.set('skip', String(skip));
    const q = p.toString();
    return request<ListeAAffecterInterventions>(`/planning/interventions/a-affecter${q ? `?${q}` : ''}`);
  },

  /** Les interventions ouvertes à venir d'une machine : la carte « Interventions prévues » de sa fiche. */
  interventionsDeMachine: (assetId: string) => request<ListeInterventionsDeMachine>(`/assets/${assetId}/planned-interventions`),

  /** Crée une intervention (ou plusieurs : une par machine, groupées dans un même passage). Rejouable par `clientKey`. */
  creerIntervention: (corps: EntreeIntervention) => request<CreationIntervention>('/planning/interventions', { method: 'POST', body: corps }),

  /** Les routes jour par jour de l'équipe : chacune rend la révision NOUVELLE, à reprendre pour l'écriture suivante. */
  ajouterJourIntervention: (id: string, revision: number, jour: string) =>
    request<RevisionIntervention>(`/planning/interventions/${id}/days`, { method: 'POST', body: { revision, jours: [jour] } }),
  retirerJourIntervention: (id: string, revision: number, jour: string) =>
    request<RevisionIntervention>(`/planning/interventions/${id}/days/remove`, { method: 'POST', body: { revision, jour } }),
  ajouterMembreIntervention: (id: string, revision: number, jour: string, technicianId: string) =>
    request<RevisionIntervention>(`/planning/interventions/${id}/members`, { method: 'POST', body: { revision, jour, technicianId, via: 'BUREAU' } }),
  /** Saisir les heures d'un technicien pour un jour (le bureau rattrape des heures oubliées) ; `null` efface. */
  /** Registre B34 : ajouter un pointage oublié (bureau, « saisie après coup »). */
  ajouterPointage: (id: string, corps: AjoutPointage) =>
    request<PointagesBureau>(`/planning/interventions/${id}/pointages`, { method: 'POST', body: corps }),
  /** Registre B34 : corriger l'heure d'un pointage, ou l'annuler. */
  corrigerPointage: (id: string, eventId: string, corps: { readonly occurredAt?: string; readonly annuler?: boolean }) =>
    request<PointagesBureau>(`/planning/interventions/${id}/pointages/${eventId}/correction`, { method: 'POST', body: corps }),
  saisirHeuresIntervention: (id: string, revision: number, jour: string, technicianId: string, minutes: number | null) =>
    request<RevisionIntervention>(`/planning/interventions/${id}/hours`, { method: 'POST', body: { revision, jour, technicianId, minutes } }),
  /** Les corrections d'heures de l'intervention (après sa validation), de la plus ancienne à la plus récente. */
  ajustementsHeures: (id: string) => request<{ readonly items: readonly AjustementHeures[] }>(`/interventions/${id}/hours-adjustments`),
  /** Corriger les heures d'un technicien un jour, APRÈS la validation (une ligne de plus, raison obligatoire). */
  ajusterHeures: (id: string, corps: { readonly jour: string; readonly technicianId: string; readonly minutes: number; readonly reason: string }) =>
    request<AjustementHeures>(`/interventions/${id}/hours-adjustments`, { method: 'POST', body: corps }),
  retirerMembreIntervention: (id: string, revision: number, jour: string, technicianId: string) =>
    request<RevisionIntervention>(`/planning/interventions/${id}/members/remove`, { method: 'POST', body: { revision, jour, technicianId } }),
  definirChefIntervention: (id: string, revision: number, jour: string, technicianId: string) =>
    request<RevisionIntervention>(`/planning/interventions/${id}/lead`, { method: 'POST', body: { revision, jour, technicianId } }),
  /** Matin / après-midi (ou `null` : dans la journée) et heure de rendez-vous d'un jour ; une clé absente ne change rien. */
  definirCreneauIntervention: (
    id: string,
    revision: number,
    jour: string,
    choix: { readonly slot?: CreneauJour | null; readonly appointmentTime?: string | null },
  ) => request<RevisionIntervention>(`/planning/interventions/${id}/days/slot`, { method: 'POST', body: { revision, jour, ...choix } }),
  /** Ranger des interventions dans la journée : la liste dans l'ordre voulu, chacune avec la révision lue. */
  ordonnerJournee: (jour: string, items: readonly { readonly interventionId: string; readonly revision: number }[]) =>
    request<{ readonly jour: string; readonly items: readonly { readonly interventionId: string; readonly rank: number; readonly revision: number }[] }>(
      '/planning/interventions/reorder',
      { method: 'POST', body: { jour, items } },
    ),
  /** Choisir (ou effacer, `null`) le palier de gamme à préparer pour une intervention : un seul chemin d'écriture, la révision en jeton. */
  choisirPalierIntervention: (id: string, revision: number, plannedLevelKey: string | null) =>
    request<RevisionIntervention>(`/planning/interventions/${id}`, { method: 'PATCH', body: { revision, plannedLevelKey } }),
  /** Enregistrer la LISTE COMPLÈTE des lignes d'une intervention (un seul chemin d'écriture, la révision en jeton). */
  modifierLignesIntervention: (id: string, corps: { readonly revision: number; readonly lignes: readonly LigneSaisieIntervention[] }) =>
    request<RevisionIntervention>(`/planning/interventions/${id}`, { method: 'PATCH', body: corps }),
  /** Corriger une intervention (nature, demande, commande, échéance, jours, équipe, durée, machine à identifier) : un seul enregistrement. */
  modifierIntervention: (id: string, corps: ModificationIntervention) =>
    request<RevisionIntervention & { readonly status: string }>(`/planning/interventions/${id}`, { method: 'PATCH', body: corps }),
  annulerIntervention: (id: string, revision: number, reason: string) =>
    request<RevisionIntervention>(`/planning/interventions/${id}/cancel`, { method: 'POST', body: { revision, reason } }),

  /** Le briefing d'une intervention : lieu, jours et équipe, lignes prévues (jamais un prix). */
  planningBriefingIntervention: (id: string) => request<BriefingIntervention>(`/planning/interventions/${id}`),

  /** La fiche de préparation d'un jour LUE depuis les interventions : même forme que celle des visites, mêmes pièces sans prix. */
  planningPreparationInterventions: (jour: string, technicianId?: string) => {
    const p = new URLSearchParams({ jour });
    if (technicianId) p.set('technicianId', technicianId);
    return request<Preparation>(`/planning/interventions/preparation?${p.toString()}`);
  },

  /** Les dates des visites d'une tournée : UNE seule fonction, côté serveur (un week-end glisse au lundi). Rien n'est écrit. */
  occurrencesTournee: (firstDay: string, frequencyMonths: number) =>
    request<{ readonly items: readonly OccurrenceTournee[] }>(
      `/planning/tours/occurrences?firstDay=${encodeURIComponent(firstDay)}&frequencyMonths=${frequencyMonths}`,
    ),
  /** Crée la tournée ET toutes ses visites en une transaction ; rejouable par `clientKey`. */
  creerTournee: (corps: EntreeTournee) => request<Tournee>('/planning/tours', { method: 'POST', body: corps }),
  tournee: (id: string) => request<Tournee>(`/planning/tours/${id}`),
  /** Libellé, n° de commande, durée, remarque seulement : ni machines ni techniciens habituels. `null` efface. */
  modifierTournee: (id: string, corps: ModificationTournee) =>
    request<TourneeModifiee>(`/planning/tours/${id}`, { method: 'PATCH', body: corps }),
  arreterTournee: (id: string, revision: number) =>
    request<TourneeArretee>(`/planning/tours/${id}/stop`, { method: 'POST', body: { revision } }),
  /** Changer le rythme = arrêter la tournée et en créer une autre, en une transaction ; rejouable par `clientKey`. */
  changerRythmeTournee: (id: string, corps: { readonly revision: number; readonly frequencyMonths: number; readonly firstDay: string; readonly clientKey?: string }) =>
    request<TourneeRythmeChange>(`/planning/tours/${id}/rhythm`, { method: 'POST', body: corps }),

  /** « Préparer l’année » : les propositions (échéances réglementaires de l’horizon), calculées à la demande, jamais stockées. */
  preparerAnnee: (mois?: number) => request<PreparationAnnee>(`/planning/prepare-year${mois === undefined ? '' : `?months=${mois}`}`),
  /** Crée une intervention « à affecter » par échéance cochée ; rejouable, un refus n’arrête pas les autres. */
  creerDepuisEcheances: (corps: { readonly natureId: string; readonly obligationIds: readonly string[]; readonly entretienIds?: readonly string[] }) =>
    request<BilanPreparationAnnee>('/planning/prepare-year', { method: 'POST', body: corps }),
  /** Les contrats d’entretien qui concernent un site, ses machines et celles du client (plusieurs par site, 2026-11-03). */
  contratsDuSite: (siteId: string) => request<ContratsDuSite>(`/planning/sites/${siteId}/contracts`),
  /** Crée un contrat depuis ce site (ses machines peuvent être sur plusieurs sites du même client). */
  creerContrat: (siteId: string, corps: EntreeContrat) =>
    request<ContratsDuSite>(`/planning/sites/${siteId}/contracts`, { method: 'POST', body: corps }),
  /** Corrige un contrat en cours (liste COMPLÈTE des machines couvertes). */
  modifierContrat: (contratId: string, siteId: string, corps: EntreeContrat) =>
    request<ContratsDuSite>(`/planning/contracts/${contratId}?site=${siteId}`, { method: 'PUT', body: corps }),
  /** Termine un contrat : il reste en historique, ne se modifie plus et libère ses machines. */
  terminerContrat: (contratId: string, siteId: string, corps: { readonly revision: number; readonly endedOn: string }) =>
    request<ContratsDuSite>(`/planning/contracts/${contratId}/end?site=${siteId}`, { method: 'POST', body: corps }),
  /** « Préparer l’année » d’un site : entretiens, visites de contrôle, échéances réglementaires. */
  preparerSite: (siteId: string, annee: 0 | 1 = 0) => request<PreparationSite>(`/planning/prepare-year/sites/${siteId}?annee=${annee}`),
  creerDepuisSite: (siteId: string, corps: CreationSite) =>
    request<BilanCreationSite>(`/planning/prepare-year/sites/${siteId}`, { method: 'POST', body: corps }),

  /** Les techniciens du planning, avec leur identifiant (une visite les désigne par lui). */
  techniciensPlanning: (avecArchives = false) =>
    request<{ readonly items: readonly Technicien[] }>(`/technicians${avecArchives ? '?includeArchived=true' : ''}`),

  creerTechnicien: (label: string) => request<Technicien>('/technicians', { method: 'POST', body: { label } }),

  /** Corriger la graphie du nom (même clé), archiver ou restaurer. Un autre nom est refusé : on crée un autre technicien. */
  /**
   * … et relier un compte (accès au téléphone, lot T1) : `membershipId` un identifiant pour relier, `null` pour délier, absent
   * pour ne rien changer. 409 si le compte est déjà relié à un autre technicien ou si le technicien a changé depuis.
   */
  modifierTechnicien: (id: string, corps: { revision: number; label?: string; archived?: boolean; membershipId?: string | null }) =>
    request<Technicien>(`/technicians/${id}`, { method: 'PATCH', body: corps }),

  /** Les comptes qu'on peut relier à un technicien (droit de gérer le planning). Jamais d'adresse électronique. */
  accesPossiblesTechniciens: () => request<{ readonly items: readonly AccesPossible[] }>('/technicians/acces-possibles'),

  /** Les absences des techniciens qui touchent la fenêtre (à partir d'aujourd'hui par défaut). */
  absencesPlanning: (depuis?: string, jusqua?: string) => {
    const p = new URLSearchParams();
    if (depuis) p.set('from', depuis);
    if (jusqua) p.set('to', jusqua);
    const q = p.toString();
    return request<{ readonly items: readonly Absence[] }>(`/planning/absences${q ? `?${q}` : ''}`);
  },

  /** Déclarer une absence : un technicien, un type, du jour au jour inclus. Jamais un refus d'affecter une visite. */
  creerAbsence: (corps: { technicianId: string; kind: GenreAbsence; from: string; to: string }) =>
    request<Absence>('/planning/absences', { method: 'POST', body: corps }),

  /** Retirer une absence (elle est archivée, jamais supprimée). */
  retirerAbsence: (id: string) => request<{ readonly ok: true }>(`/planning/absences/${id}/remove`, { method: 'POST', body: {} }),

  /** La journée de travail, en minutes (420 par défaut). */
  capacitePlanning: () => request<{ readonly dayMinutes: number }>('/planning/capacity'),

  definirCapacitePlanning: (dayMinutes: number) =>
    request<{ readonly dayMinutes: number }>('/planning/capacity', { method: 'PATCH', body: { dayMinutes } }),

  /** Les heures PRÉVUES par technicien et par type sur une période (366 jours au plus). */
  heuresPrevues: (from: string, to: string) => request<HeuresPrevues>(`/planning/hours?from=${from}&to=${to}`),

  // ------------------------------------------------- tarifs de main d'œuvre et chiffrage (lot U3, registre B31)

  /** Les tarifs de main d'œuvre et de déplacement, du plus récent au plus ancien. Réservé à qui voit les prix. */
  tarifsMo: (archives = false) => request<ListeTarifsMo>(`/pricing/labor-rates${archives ? '?archived=true' : ''}`),

  /** Définit un tarif DATÉ : il ne modifie jamais un précédent, il s'y ajoute. */
  definirTarifMo: (corps: EntreeTarifMo) => request<TarifMo>('/pricing/labor-rates', { method: 'POST', body: corps }),

  /** « Cette ligne n'a jamais existé » : correction d'une faute de saisie, définitive. */
  archiverTarifMo: (id: string) => request<void>(`/pricing/labor-rates/${id}/archive`, { method: 'POST' }),

  /** Les tarifs qui s'appliquent à un site un jour donné, avec leur provenance ou « non paramétré ». N'écrit rien. */
  tarifsDuJour: (siteId: string | null, jour: string) => {
    const requete = new URLSearchParams({ jour });
    if (siteId) requete.set('siteId', siteId);
    return request<TarifsDuJourVue>(`/pricing/labor-rates/resolve?${requete.toString()}`);
  },

  /** Ce que coûte une intervention : main d'œuvre, déplacement, pièces. Réservé à qui voit les prix. N'écrit rien. */
  chiffrageIntervention: (id: string) => request<ChiffrageInterventionVue>(`/planning/interventions/${id}/pricing`),
  /** Le forfait d'une tournée (lot U4) : donnée financière, réservée à qui voit les prix. */
  forfaitTournee: (id: string) => request<ForfaitTourneeVue>(`/planning/tours/${id}/forfait`),
  /** Poser, changer ou retirer (`montant: null`) le forfait ; `revision` = le jeton de la tournée. Réservé à qui écrit les prix. */
  definirForfaitTournee: (id: string, corps: { revision: number; libelle?: string | null; montant: string | null }) =>
    request<ForfaitTourneeVue>(`/planning/tours/${id}/forfait`, { method: 'PUT', body: corps }),
};

// ------------------------------------------------- tarifs de main d'œuvre et chiffrage d'une intervention (lot U3)

export type PorteeTarifMo = 'TENANT' | 'PARTY' | 'SITE';
export type ChampTarifMo = 'laborDay' | 'laborNight' | 'laborWeekend' | 'laborHoliday' | 'travel';

export interface TarifMo {
  readonly id: string;
  readonly portee: PorteeTarifMo;
  readonly partyId: string | null;
  readonly partyName: string | null;
  readonly siteId: string | null;
  readonly siteName: string | null;
  /** AAAA-MM-JJ. */
  readonly validFrom: string;
  readonly laborDay: string | null;
  readonly laborNight: string | null;
  readonly laborWeekend: string | null;
  readonly laborHoliday: string | null;
  readonly travel: string | null;
  readonly note: string | null;
  readonly archived: boolean;
  readonly createdAt: string;
}

export interface ListeTarifsMo {
  readonly items: readonly TarifMo[];
}

export interface EntreeTarifMo {
  readonly portee: PorteeTarifMo;
  readonly partyId?: string | null;
  readonly siteId?: string | null;
  /** Euros HT au point décimal ; vide ou null = « hérite ». */
  readonly montants: Readonly<Partial<Record<ChampTarifMo, string | null>>>;
  readonly validFrom: string;
  readonly note?: string | null;
  /** « Modifier » : le tarif remplacé. Même date d'effet = correction (l'ancien est archivé) ; plus tard = changement de prix. */
  readonly remplace?: string | null;
}

export interface TarifRetenuVue {
  /** `null` = non paramétré. */
  readonly montant: string | null;
  readonly portee: PorteeTarifMo | null;
  readonly validFrom: string | null;
}

export interface TarifsDuJourVue {
  readonly jour: string;
  readonly categorie: 'FERIE' | 'WEEKEND' | 'OUVRE';
  readonly champHoraire: ChampTarifMo;
  readonly horaire: TarifRetenuVue;
  readonly deplacement: TarifRetenuVue;
  readonly tous: Readonly<Record<ChampTarifMo, TarifRetenuVue>>;
}

export interface ForfaitTourneeVue {
  readonly tourId: string;
  /** Jeton de concurrence de la tournée. */
  readonly revision: number;
  /** `null` = aucun forfait sur cette tournée ; le montant est en euros HT, deux décimales (« 450.50 »). */
  readonly forfait: { readonly libelle: string | null; readonly montant: string } | null;
}

export interface LigneChiffreeVue {
  /** COMPRIS : la visite d'un contrat annuel (chiffrée pour la rentabilité, jamais facturée) ; A_FACTURER : tout le reste. */
  readonly facturation: 'COMPRIS' | 'A_FACTURER';
  readonly genre: 'MAIN_D_OEUVRE' | 'DEPLACEMENT' | 'FORFAIT' | 'PIECE' | 'AUTRE';
  readonly libelle: string;
  readonly detail: string | null;
  readonly jour: string | null;
  readonly quantite: string | null;
  readonly unite: string | null;
  /** Euros HT ; `null` = non chiffré (voir `raison`). Main d'œuvre : tarif de l'HEURE. */
  readonly prixUnitaire: string | null;
  readonly total: string | null;
  readonly coutAchat: string | null;
  readonly raison: string | null;
  readonly compte: boolean;
  /** Le `lineKey` de la ligne enregistrée dont vient ce chiffrage (pièces, déplacement, forfait) ; `null` pour la main d'œuvre ou une pièce lue dans la gamme. */
  readonly cle: string | null;
}

export interface ChiffrageInterventionVue {
  readonly interventionId: string;
  readonly statut: string;
  readonly lignes: readonly LigneChiffreeVue[];
  /** Le choix du bon : contrat annuel, facturé à la visite, ou pas encore choisi. */
  readonly facturationBon: FacturationBon | null;
  /** Le total À FACTURER, euros HT. */
  readonly totalHt: string;
  /** Le total COMPRIS au contrat annuel (pour la rentabilité), euros HT. */
  readonly totalComprisHt: string;
  readonly coutAchatPieces: string;
  /** Vrai quand au moins une ligne qui devrait l'être n'est pas chiffrée : le total est un minimum. */
  readonly incomplet: boolean;
  readonly nonChiffrees: number;
  readonly pasFacturation: number;
  /** Vrai : figé à la validation, il ne bouge plus quand les tarifs changent. Faux : calculé d'après les tarifs d'aujourd'hui. */
  readonly fige: boolean;
  /** Déplacement automatique non facturé : pour ce passage, pour toute la tournée, ou pas retiré (null). */
  readonly deplacementRetire: 'INTERVENTION' | 'TOURNEE' | null;
}

// --- Tiers et imports (Lot 1) ------------------------------------------------

export type PartyRoleKind =
  | 'CUSTOMER'
  | 'SUPPLIER'
  | 'SUBCONTRACTOR'
  | 'CONTROL_BODY'
  | 'MANUFACTURER'
  | 'BRAND';

export interface Party {
  readonly id: string;
  readonly legalName: string;
  readonly tradeName: string | null;
  readonly legalIdentifier: string | null;
  readonly vatNumber: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly postalCode: string | null;
  readonly city: string | null;
  readonly status: 'ACTIVE' | 'DORMANT' | 'ARCHIVED';
  readonly roles: readonly PartyRoleKind[];
  readonly sourceSystem: string | null;
}

export type RowOutcome = 'READY' | 'WARNING' | 'ERROR' | 'DUPLICATE' | 'INCOMPLETE';

export interface ImportRowReport {
  readonly lineNumber: number;
  readonly outcome: RowOutcome;
  readonly messages: readonly string[];
  readonly preview: Record<string, unknown> | null;
}

export interface ImportReport {
  readonly jobId: string;
  readonly fileName: string;
  readonly status: 'SIMULATED' | 'COMMITTED';
  readonly totals: {
    readonly total: number;
    readonly ready: number;
    readonly warning: number;
    readonly error: number;
    readonly duplicate: number;
    readonly incomplete: number;
  };
  readonly created: number;
  readonly rows: readonly ImportRowReport[];
}


// --- Sites et machines (Lot 1) -----------------------------------------------

export interface Site {
  readonly id: string;
  readonly customerId: string;
  readonly customerName: string;
  readonly name: string;
  readonly code: string | null;
  readonly addressLine1: string | null;
  readonly postalCode: string | null;
  readonly city: string | null;
  readonly accessNotes: string | null;
  readonly openingHours: string | null;
  readonly safetyNotes: string | null;
  readonly zone: string | null;
  readonly status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  readonly assetCount: number;
}

export type AssetStatus =
  | 'IN_SERVICE'
  | 'OUT_OF_SERVICE'
  | 'IN_MAINTENANCE'
  | 'IN_STOCK'
  | 'DECOMMISSIONED';

export type Completeness = 'PROVIDED' | 'MISSING' | 'UNAVAILABLE';

export type AttributeDataType = 'TEXT' | 'NUMBER' | 'BOOLEAN' | 'DATE' | 'ENUM';

export interface AssetAttribute {
  readonly definitionId: string;
  readonly code: string;
  readonly label: string;
  readonly dataType: AttributeDataType;
  readonly unit: string | null;
  readonly options: readonly string[];
  readonly expected: boolean;
  readonly completeness: Completeness;
  readonly value: string | null;
  readonly unavailableReason: string | null;
}

export interface Asset {
  readonly id: string;
  readonly qrCode: string;
  readonly assetType: string;
  readonly label: string | null;
  readonly model: string | null;
  readonly manufacturerReference: string | null;
  readonly clientDesignation: string | null;
  readonly serialNumber: string | null;
  readonly location: string | null;
  readonly status: AssetStatus;
  readonly commissionedAt: string | null;
  readonly notes: string | null;
  readonly siteId: string | null;
  readonly siteName: string | null;
  readonly siteCity: string | null;
  readonly customerId: string | null;
  readonly customerName: string | null;
  readonly manufacturerName: string | null;
  readonly brandName: string | null;
  readonly manufacturerPartyId: string | null;
  readonly brandPartyId: string | null;
  readonly ownerPartyId: string | null;
  readonly ownerName: string | null;
  readonly sourceReference: string | null;
  readonly attributes: readonly AssetAttribute[];
  readonly missingCount: number;
  /** Lot T4 : « à valider » = créée sur le téléphone par un technicien, pas encore validée par le bureau. */
  readonly reviewStatus: 'VALIDATED' | 'TO_VALIDATE';
  /** Lot T4 : la fiche qui remplace celle-ci (« c'est une machine existante ») ; la fiche est alors archivée. */
  readonly mergedIntoAssetId: string | null;
  /** Lot T4 : les fiches en double fusionnées dans celle-ci (archivées, leur historique y reste). */
  readonly mergedFrom: readonly { readonly id: string; readonly label: string | null; readonly qrCode: string }[];
  /** Jeton de concurrence de la fiche, exigé pour valider ou fusionner. */
  readonly revision: number;
}

/** Lot T4 : une machine créée sur le téléphone, à valider par le bureau. */
export interface MachineAValider {
  readonly id: string;
  readonly revision: number;
  readonly qrCode: string;
  readonly libelle: string | null;
  readonly nature: string;
  readonly marque: { readonly id: string; readonly nom: string } | null;
  readonly modele: string | null;
  /** Nul = illisible (ou non relevé) sur place. */
  readonly numeroSerie: string | null;
  readonly nomClient: string | null;
  readonly site: { readonly id: string; readonly nom: string } | null;
  readonly client: { readonly id: string; readonly nom: string } | null;
  readonly creePar: string | null;
  readonly creeLe: string;
  readonly bonOrigine: { readonly id: string; readonly numero: string | null } | null;
  readonly photos: number;
}

export interface MachinesAValider {
  readonly items: readonly MachineAValider[];
  readonly total: number;
}

export interface FusionMachine {
  readonly cibleId: string;
  readonly doublonId: string;
  readonly bonsDeplaces: number;
  readonly bonsRestes: number;
  readonly relevesRestes: number;
  readonly entretiensRestes: number;
  readonly compteursCrees: number;
  readonly valeursRetirees: number;
}

export interface AssetListResult {
  readonly items: readonly Asset[];
  readonly total: number;
}

export interface AssetFacets {
  readonly customers: readonly { readonly id: string; readonly legalName: string; readonly activeAssetCount: number }[];
  readonly sites: readonly { readonly id: string; readonly customerId: string; readonly name: string; readonly assetCount: number }[];
  readonly byNature: readonly { readonly assetType: string; readonly count: number }[];
  readonly thirdPartyOwnedCount: number;
  readonly espCount: number;
  readonly incompleteCount: number;
  readonly total: number;
}

export interface AttributeDefinition {
  readonly id: string;
  readonly code: string;
  readonly label: string;
  readonly dataType: AttributeDataType;
  readonly unit: string | null;
  readonly appliesToAssetType: string | null;
  readonly options: readonly string[];
  readonly expected: boolean;
  readonly position: number;
}

export interface AdresseEspace {
  readonly line1: string | null;
  readonly line2: string | null;
  readonly postalCode: string | null;
  readonly city: string | null;
  readonly countryCode: string | null;
}

/** En-tête du menu : nom de l'espace, et étiquette d'environnement hors production. */
export interface IdentiteEspace {
  /** L'identifiant de l'espace : propriétaire des logos et des documents de l'entreprise (`OwnerType` `tenant`). */
  readonly espaceId: string;
  readonly nom: string;
  readonly environnement: string | null;
  /** L'adresse de l'entreprise en lignes imprimables (voie, complément, « code postal ville »). */
  readonly adresse: readonly string[];
  readonly telephone: string | null;
  readonly email: string | null;
  readonly siteWeb: string | null;
  /** Le logo de l'entreprise (un fichier de l'espace, propriétaire `tenant`) ; `null` = aucun. */
  readonly logoPrincipalId: string | null;
  /** Les logos des partenaires et certifications (trois au plus), dans l'ordre d'impression. */
  readonly logosSecondairesIds: readonly string[];
}

export interface Settings {
  readonly legalName: string | null;
  readonly legalIdentifier: string | null;
  readonly vatNumber: string | null;
  readonly address: AdresseEspace | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly website: string | null;
  readonly logoFileId: string | null;
  readonly secondaryLogoFileIds: readonly string[];
  readonly primaryColor: string | null;
  readonly accentColor: string | null;
  readonly customDomain: string | null;
  readonly currency: string;
  readonly fiscalYearStartMonth: number;
  readonly planningHorizonMonths: number;
  /** Lecture seule : dépend des arbitrages A10 et A12, non rendus. */
  readonly fieldDefaults: {
    readonly screenLockMinutes: number;
    readonly sessionEndHour: number;
    readonly offlineMaxDays: number;
    readonly geolocationEnabled: boolean;
    readonly geolocationRetentionDays: number;
  };
}

export type MembershipStatus = 'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'REVOKED';

export interface Membre {
  readonly membershipId: string;
  readonly email: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly jobTitle: string | null;
  readonly status: MembershipStatus;
  readonly roles: readonly { id: string; code: string; name: string }[];
  readonly mfaEnabled: boolean;
  readonly lastLoginAt: string | null;
  readonly invitedAt: string;
  readonly acceptedAt: string | null;
  readonly invitationEnCours: boolean;
}

export interface RoleEspace {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly description: string | null;
  readonly mfaObligatoire: boolean;
  readonly membres: number;
}

export interface InvitationCreee {
  readonly membre: Membre;
  /** Rendu UNE SEULE FOIS. Non stocké : perdu, il faut réinviter. */
  readonly token: string;
  readonly expiresAt: string;
}

export interface Invitation {
  readonly email: string;
  readonly tenantName: string;
  readonly roles: readonly string[];
  readonly mfaObligatoire: boolean;
}

// --- Pièces jointes ----------------------------------------------------------

/** Objets auxquels une pièce jointe peut se rattacher (allow-list côté API). */
export type OwnerType = 'asset' | 'party' | 'site' | 'compliance_obligation' | 'intervention' | 'tenant';

export interface PieceJointe {
  readonly id: string;
  readonly originalName: string;
  /** Nom choisi par l'utilisateur ; `null` tant qu'il n'a pas renommé. */
  readonly label: string | null;
  readonly contentType: string;
  readonly byteSize: number;
  /** Décidé par le serveur d'après le type RÉEL des octets, pas par l'extension. */
  readonly estImage: boolean;
  readonly createdAt: string;
  readonly createdBy: string | null;
  /** Identifiant du document, commun à toutes ses versions. */
  readonly lineageId: string;
  /** Numéro de version, à partir de 1. */
  readonly revision: number;
  /** Vrai si une version plus récente a été déposée. */
  readonly superseded: boolean;
  readonly categoryId: string | null;
  readonly categoryLabel: string | null;
}

/** Catégorie de document, configurée par l'espace — aucune n'est imposée. */
export interface CategorieDocument {
  readonly id: string;
  readonly code: string;
  readonly label: string;
  /** Types d'objets pour lesquels elle est proposée. Vide = partout. */
  readonly appliesTo: readonly OwnerType[];
  readonly position: number;
}

// --- Étiquettes QR ------------------------------------------------------------

export interface EtiquetteQr {
  readonly assetId: string;
  readonly qrCode: string;
  /** Contenu exact encodé dans le QR. */
  readonly contenu: string;
  /** Image SVG en `data:`, affichée par une balise image. */
  readonly qrImage: string;
  readonly label: string | null;
  readonly assetType: string;
  readonly model: string | null;
  readonly serialNumber: string | null;
  readonly siteName: string | null;
  readonly customerName: string | null;
  readonly location: string | null;
}

export interface PlancheQr {
  readonly etiquettes: readonly EtiquetteQr[];
  /** Machines correspondant à la demande, avant plafonnement. */
  readonly total: number;
  readonly tronquee: boolean;
}

// --- Compteurs ---------------------------------------------------------------

export type SourceReleve = 'DESK' | 'FIELD' | 'IMPORT' | 'INTERVENTION';

export interface Releve {
  readonly id: string;
  /** Chaîne et non nombre : la précision d'un index ne doit pas se perdre. */
  readonly value: string;
  readonly readAt: string;
  readonly source: SourceReleve;
  readonly note: string | null;
  /** Incohérence constatée à l'enregistrement (CDC §16) : signalée, jamais bloquante. */
  readonly anomaly: boolean;
  readonly anomalyReason: string | null;
  readonly correctsReadingId: string | null;
  /** Le bon d'intervention validé qui a écrit ce relevé (lot I1.2) ; `null` pour un relevé saisi ailleurs. */
  readonly interventionId: string | null;
  readonly createdAt: string;
  readonly createdBy: string | null;
}

export interface Compteur {
  readonly id: string;
  readonly assetId: string;
  readonly label: string;
  readonly unit: string | null;
  /** Faux si le compteur peut revenir à zéro (carte remplacée). */
  readonly monotonic: boolean;
  /** C'est LE compteur qui commande les entretiens de la machine (ses heures totales). */
  readonly drivesMaintenance: boolean;
  readonly dernier: Releve | null;
  readonly nombreReleves: number;
}

// --- Le prochain entretien d'une machine (lot B) -------------------------------

export type EtatEntretien = 'A_JOUR' | 'BIENTOT' | 'EN_RETARD' | 'A_CONFIRMER' | 'A_RENSEIGNER';

export type CodeManqueEntretien =
  | 'AUCUN_COMPTEUR'
  | 'COMPTEUR_NON_DESIGNE'
  | 'AUCUN_RELEVE'
  | 'RELEVE_A_VERIFIER'
  | 'GAMME_SANS_VERSION'
  | 'GAMME_SANS_SEUIL_HEURES'
  | 'GAMME_INVALIDE'
  | 'ENTRETIEN_SANS_HEURES'
  | 'COMPTEUR_INFERIEUR_A_UN_ENTRETIEN'
  | 'DROIT_COMPTEURS';

export interface ManqueEntretien {
  readonly code: CodeManqueEntretien;
  /** Le message est écrit par le serveur, en français, avec ce qu'il faut saisir. */
  readonly message: string;
}

export interface AvertissementEntretien {
  readonly code: string;
  readonly message: string;
}

export interface PointAVenirEntretien {
  readonly code: string;
  readonly libelle: string | null;
  /** Le point visé, en heures. */
  readonly cible: number;
  /** Heures restantes avant ce point. */
  readonly reste: number;
  /** AAAA-MM-JJ ; `null` tant qu'aucun régime ne permet d'estimer une date. */
  readonly dateEstimee: string | null;
  readonly absorbes: readonly string[];
}

export interface PointEnRetardEntretien {
  readonly code: string;
  readonly libelle: string | null;
  readonly point: number;
  readonly depassement: number;
}

export interface RegimeEntretien {
  readonly heuresParAn: number;
  readonly origine: 'MESURE' | 'DECLARE' | 'CONTRAT';
}

export interface ReglagesEntretien {
  /** Heures de marche par an déclarées sur la machine ; `null` = aucune. */
  readonly declaredHoursPerYear: number | null;
  /** Le déclenchement CHOISI ; `null` = celui que dit la gamme (voir `mode` du prochain entretien). Absent d'une réponse d'avant le 2026-10-30. */
  readonly triggerMode?: ModeDeclenchement | null;
  /** Le nombre de mois choisi pour le calendrier ; `null` = celui de la gamme, 12 sinon. */
  readonly calendarMonths?: number | null;
  /** Jeton de concurrence ; `null` tant qu'aucun réglage n'a été enregistré. */
  readonly version: number | null;
}

export type ModeDeclenchement = 'COUNTER' | 'CALENDAR';

/** Au calendrier : le palier unique, le rythme, et l'échéance comptée depuis le dernier entretien fait. */
export interface CalendrierEntretien {
  readonly code: string;
  readonly libelle: string | null;
  readonly levelKey: string | null;
  readonly mois: number;
  readonly origineMois: 'REGLAGE' | 'GAMME' | 'DEFAUT';
  readonly depuis: string | null;
  readonly echeance: string | null;
  readonly joursRestants: number | null;
}

export interface ProchainEntretien {
  readonly etat: EtatEntretien;
  /** Le déclenchement retenu : choisi sur la machine, ou déduit (de la gamme, ou de l'absence de compteur). */
  readonly mode?: { readonly mode: ModeDeclenchement; readonly origine: 'REGLAGE' | 'GAMME' | 'SANS_COMPTEUR' };
  /** Au calendrier : le palier, le rythme et l'échéance ; `null` au compteur. */
  readonly calendrier?: CalendrierEntretien | null;
  /** Le nombre de paliers de la gamme en vigueur (le calendrier n'en admet qu'un). */
  readonly nombrePaliers?: number;
  readonly prochain: PointAVenirEntretien | null;
  readonly enRetard: PointEnRetardEntretien | null;
  /** Points de la grille déjà franchis par le compteur. */
  readonly pointsFranchis: number;
  readonly valeur: { readonly value: string; readonly readAt: string; readonly joursDepuisReleve: number } | null;
  readonly regime: RegimeEntretien | null;
  readonly manques: readonly ManqueEntretien[];
  readonly avertissements: readonly AvertissementEntretien[];
  readonly gamme: {
    readonly id: string;
    readonly code: string;
    readonly label: string;
    readonly versionNumber: number | null;
    readonly validFrom: string | null;
  };
  /** Le compteur désigné pour les entretiens ; `null` = aucun. */
  readonly compteur: { readonly id: string; readonly label: string; readonly unit: string | null } | null;
  readonly nombreCompteurs: number;
  readonly peutLireCompteurs: boolean;
  readonly reglages: ReglagesEntretien;
  /** Le jour utilisé pour le calcul (celui du fuseau du locataire), AAAA-MM-JJ. */
  readonly aujourdhui: string;
  /** Le dernier entretien réalisé EFFECTIF (ni rectifié, ni annulé) ; `null` = aucun. */
  readonly dernierEntretien: DernierEntretien | null;
  /** Présent seulement quand l'état est « bientôt » : la date proche, ou presque plus d'heures. */
  readonly bientotPar?: 'DATE' | 'HEURES';
}

export interface DernierEntretien {
  readonly id: string;
  readonly levelCode: string;
  /** AAAA-MM-JJ. */
  readonly performedOn: string;
  readonly counterValue: string | null;
}

/** Un entretien RÉALISÉ d'une machine (lot B2). Rien ne s'efface : un entretien corrigé reste lisible. */
export interface EntretienRealise {
  readonly id: string;
  readonly levelKey: string;
  readonly levelCode: string;
  /** AAAA-MM-JJ. */
  readonly performedOn: string;
  /** Les heures lues au compteur désigné, en chaîne ; `null` = non renseignées. */
  readonly counterValue: string | null;
  readonly counterId: string | null;
  /** Le relevé de compteur écrit (ou relié) avec cet entretien. */
  readonly readingId: string | null;
  readonly note: string | null;
  readonly source: 'DESK' | 'IMPORT' | 'INTERVENTION';
  readonly correctsRecordId: string | null;
  /** Le bon d'intervention validé qui a écrit cet entretien (lot I1.2) ; `null` pour un entretien saisi ailleurs. */
  readonly interventionId: string | null;
  readonly createdAt: string;
  readonly createdBy: string | null;
  /** Une rectification l'a remplacé. */
  readonly remplacee: boolean;
  /** Une annulation l'a annulé. */
  readonly annulee: boolean;
  /** Il compte pour le calcul : ni remplacé, ni annulé. */
  readonly effectif: boolean;
  readonly annulation: { readonly createdAt: string; readonly createdBy: string | null; readonly note: string | null } | null;
}

export interface ListeEntretiensRealises {
  readonly items: readonly EntretienRealise[];
  readonly total: number;
}

export interface EntreeEntretienRealise {
  readonly levelKey: string;
  /** AAAA-MM-JJ. */
  readonly performedOn: string;
  /** Les heures, en chaîne (« 22577 », « 1250,5 ») ; absent ou vide = non renseignées. */
  readonly counterValue?: string | null;
  readonly note?: string | null;
  /** Écrire aussi un relevé du compteur désigné, à la date de l'entretien (même transaction). */
  readonly releverAussi?: boolean;
  /** L'entretien que celui-ci rectifie. */
  readonly corrigeId?: string | null;
}

export interface ReleveEcritAvecEntretien {
  readonly id: string;
  readonly value: string;
  readonly readAt: string;
  readonly anomaly: boolean;
  readonly anomalyReason: string | null;
  /** Vrai : relevé créé par cet enregistrement ; faux : un relevé identique existait déjà, il est relié. */
  readonly cree: boolean;
  /** Vrai : le relevé lié à l'entretien rectifié a été rectifié à son tour. */
  readonly rectifie: boolean;
}

export interface EnregistrementEntretien {
  readonly entretien: EntretienRealise;
  readonly releve: ReleveEcritAvecEntretien | null;
  /** Ce que l'écran doit dire en plus (un ancien relevé qui reste, un relevé signalé…). */
  readonly messages: readonly string[];
}

// --- Établissements et contacts ----------------------------------------------

export type TypeCanal = 'EMAIL' | 'PHONE' | 'MOBILE';
export type UsageCanal = 'GENERAL' | 'QUOTE' | 'ORDER' | 'INVOICE' | 'INTERVENTION' | 'ALERT';

export interface Canal {
  readonly id: string;
  readonly type: TypeCanal;
  readonly value: string;
  /** Usages servis. Vide = jamais choisi comme destinataire d'un envoi. */
  readonly usages: readonly UsageCanal[];
  readonly isPrimary: boolean;
  readonly consentGivenAt: string | null;
  readonly consentWithdrawnAt: string | null;
  readonly consentementActif: boolean;
}

export interface Contact {
  readonly id: string;
  readonly partyId: string;
  readonly establishmentId: string | null;
  readonly establishmentLabel: string | null;
  readonly firstName: string | null;
  readonly lastName: string;
  readonly jobTitle: string | null;
  readonly isPrimary: boolean;
  readonly notes: string | null;
  readonly channels: readonly Canal[];
}

export interface Etablissement {
  readonly id: string;
  readonly partyId: string;
  readonly label: string;
  readonly legalIdentifier: string | null;
  readonly addressLine1: string | null;
  readonly addressLine2: string | null;
  readonly postalCode: string | null;
  readonly city: string | null;
  readonly countryCode: string | null;
  readonly isBillingEntity: boolean;
  readonly notes: string | null;
  readonly nombreContacts: number;
}

// --- Conformité réglementaire (CDC §6.4) --------------------------------------

export interface ComplianceAlerts {
  readonly beforeDueMonths: readonly number[];
}

/** Un jalon indépendant : ESP porte « IP » (inspection périodique) et « RP » (requalification). */
export interface FixedScheduleMilestone {
  readonly code: string;
  readonly label: string;
  readonly initialDelayMonths: number;
  /** Exception optionnelle pour les machines mises en service avant une date charnière. */
  readonly initialDelayMonthsIfCommissionedBefore?: {
    readonly beforeDate: string;
    readonly delayMonths: number;
  };
  readonly recurrenceMonths: number;
  readonly alerts: ComplianceAlerts;
}

export interface FixedScheduleFormula {
  readonly kind: 'FIXED_SCHEDULE';
  readonly milestones: readonly FixedScheduleMilestone[];
}

export interface ThresholdTier {
  readonly minQuantity: number;
  readonly recurrenceMonths: number;
}

/** F-Gaz : la périodicité dépend d'une quantité calculée depuis deux champs techniques. */
export interface ThresholdTableFormula {
  readonly kind: 'THRESHOLD_TABLE';
  readonly milestoneCode: string;
  readonly label: string;
  readonly quantityAttributeCode: string;
  readonly fluidAttributeCode: string;
  /** PRG (pouvoir de réchauffement global) par fluide. */
  readonly gwpTable: Readonly<Record<string, number>>;
  readonly thresholds: readonly ThresholdTier[];
  /** Champ technique booléen qui double les seuils quand il vaut vrai. */
  readonly doublingAttributeCode?: string;
  readonly alerts: ComplianceAlerts;
}

export type ComplianceFormula = FixedScheduleFormula | ThresholdTableFormula;

/** Une version, IMMUABLE une fois publiée — comme les versions de documents. */
export interface ComplianceRuleVersion {
  readonly id: string;
  readonly versionNumber: number;
  readonly validFrom: string;
  readonly validTo: string | null;
  readonly source: string;
  readonly formula: ComplianceFormula;
  readonly motif: string;
  readonly publishedAt: string | null;
  readonly publishedBy: string | null;
}

export interface ComplianceRule {
  readonly id: string;
  readonly code: string;
  readonly label: string;
  readonly jurisdiction: string;
  readonly ruleType: string;
  readonly archivedAt: string | null;
  readonly versions: readonly ComplianceRuleVersion[];
}

/** Rattachement explicite d'une règle à une machine — jamais automatique. */
export interface ComplianceRuleAssignment {
  readonly id: string;
  readonly ruleId: string;
  readonly ruleCode: string;
  readonly ruleLabel: string;
  readonly assignedAt: string;
  readonly assignedBy: string | null;
}

export type ComplianceOutcome = 'OK' | 'RESERVES' | 'REFUSED';
export type ComplianceUrgency = 'PENDING' | 'DUE_SOON' | 'OVERDUE' | 'DONE';

export interface ComplianceObligation {
  readonly id: string;
  readonly assetId: string;
  readonly ruleId: string;
  readonly ruleCode: string;
  readonly ruleLabel: string;
  readonly milestoneCode: string;
  readonly label: string;
  readonly dueDate: string;
  readonly urgency: ComplianceUrgency;
  readonly organismePartyId: string | null;
  readonly completedAt: string | null;
  readonly completedOutcome: ComplianceOutcome | null;
  readonly completedNote: string | null;
}

// --- Gammes d'entretien (CDC §6.3, §8.3) --------------------------------------

export type DimensionSeuil = 'HOURS' | 'MONTHS';
export type GenreLigneGamme = 'REPLACE' | 'CHECK' | 'CONDITIONAL';

/** Déclencheur d'un palier : `first + k × interval`, ou une seule fois si `interval` est nul. */
export interface GammeSeuil {
  readonly dimension: DimensionSeuil;
  readonly first: number;
  readonly interval: number | null;
}

/**
 * Une ligne de palier. Les clés de prix sont ABSENTES (pas `null`) quand
 * l'appelant n'a pas le droit de les voir : il faut tester leur présence.
 */
export interface GammeLigne {
  readonly lineKey: string;
  readonly kind: GenreLigneGamme;
  readonly designation: string;
  /** `null` = quantité inconnue. */
  readonly quantity: number | null;
  readonly unit: string | null;
  readonly note: string | null;
  readonly reference: string | null;
  readonly adaptableReference: string | null;
  readonly adaptableSupplierPartyId: string | null;
  readonly publicPrice?: number | null;
  readonly internalPrice?: number | null;
}

export interface GammeNiveau {
  readonly levelKey: string;
  readonly code: string;
  readonly label: string;
  /** Plus englobant = plus grand : à un même point, ce palier s'applique SEUL. */
  readonly rank: number;
  /** Minutes de main d'œuvre prévues pour ce palier, PAR TECHNICIEN (lot B23) ; absent = non renseigné. Du temps, jamais un prix. */
  readonly laborMinutes?: number | null;
  readonly thresholds: readonly GammeSeuil[];
  readonly lines: readonly GammeLigne[];
}

export interface GammeContenu {
  readonly levels: readonly GammeNiveau[];
}

export interface EcheanceTheorique {
  readonly dimension: DimensionSeuil;
  readonly valeur: number;
  readonly code: string;
  readonly absorbes: readonly string[];
}

/**
 * Les prix calculés à la publication pour UNE ligne (lot P2, registre B26) : le coût d'achat réel (celui de
 * l'équivalent quand la ligne en a un), le prix de VENTE (ce que voit le client, « Prix public HT ») et les deux
 * conditions d'achat qui les ont produits. `null` = non calculé (condition absente…), jamais zéro.
 */
export interface PrixCalculeLigne {
  readonly lineKey: string;
  readonly purchasePrice: number | null;
  readonly salePrice: number | null;
  readonly purchaseConditionId: string | null;
  readonly saleConditionId: string | null;
}

export type StatutVersionGamme = 'BROUILLON' | 'PUBLIEE' | 'ARCHIVEE';

/** Une version : brouillon modifiable, ou publiée donc figée. */
export interface GammeVersion {
  readonly id: string;
  readonly versionNumber: number;
  /**
   * Jeton de concurrence : à renvoyer en enregistrant un brouillon. S'il a changé depuis, l'API
   * refuse (409) plutôt que d'écraser le travail de quelqu'un d'autre.
   */
  readonly revision: number;
  readonly validFrom: string;
  readonly source: string;
  readonly motif: string;
  readonly statut: StatutVersionGamme;
  readonly publishedAt: string | null;
  readonly publishedBy: string | null;
  readonly archivedAt: string | null;
  readonly contenu: GammeContenu;
  /**
   * Lot P2 — les prix d'achat et de vente CALCULÉS à la publication, une entrée par ligne qui en porte au moins un,
   * jointe à `contenu` par `lineKey`. Jamais dans `contenu` : une ligne de gamme garde la forme d'un brouillon.
   * ABSENT (pas vide) pour un brouillon et pour qui ne voit pas les prix.
   */
  readonly prixCalcules?: readonly PrixCalculeLigne[];
  /** Points théoriques calculés par le moteur : grille du compteur, et mois. */
  readonly echeances: {
    readonly heures: readonly EcheanceTheorique[];
    readonly mois: readonly EcheanceTheorique[];
  };
}

export interface GammeCritere {
  readonly id: string;
  readonly modelKey: string;
  readonly manufacturerReferenceKey: string;
  /**
   * Texte tel que saisi, pour l'affichage ; `null` pour un critère créé avant que le texte soit
   * conservé (on montre alors la clé). La comparaison se fait toujours sur la clé.
   */
  readonly modelLabel: string | null;
  readonly manufacturerReferenceLabel: string | null;
}

/** Un critère de suggestion tel que l'API l'attend : un modèle, et si besoin une référence. */
export interface CritereGammeInput {
  readonly model: string;
  readonly manufacturerReference?: string;
}

export interface CreerGammeInput {
  /** Facultatif : l'API le déduit de la marque et du premier modèle (`KAESER_SK24`). */
  readonly code?: string;
  readonly label: string;
  readonly brandPartyId: string;
  readonly criteres: readonly CritereGammeInput[];
  readonly referencesConstructeur?: readonly string[];
  /**
   * Copier le contenu de cette version — paliers, lignes, prix — en version 1 brouillon de la nouvelle
   * gamme. La marque doit être celle de la gamme d'origine.
   */
  readonly depuisVersionId?: string;
}

/** Modification de l'identité d'une gamme : un champ absent n'est pas touché. */
export interface ModifierGammeInput {
  readonly label?: string;
  readonly referencesConstructeur?: readonly string[];
  /** Liste COMPLÈTE : un modèle absent est archivé, un modèle ajouté est créé. */
  readonly criteres?: readonly CritereGammeInput[];
}

export interface GammeResume {
  readonly id: string;
  readonly code: string;
  readonly label: string;
  readonly scope: 'SHARED' | 'INDIVIDUAL';
  readonly brandPartyId: string;
  readonly brandName: string;
  readonly criteres: readonly GammeCritere[];
  /** Références constructeur pour lesquelles les pièces sont établies ; vide : aucune variante. */
  readonly referencesConstructeur: readonly string[];
  readonly archivedAt: string | null;
  readonly enVigueur: { readonly versionNumber: number; readonly validFrom: string } | null;
  readonly brouillons: number;
  readonly machinesRattachees: number;
}

export interface MachineCorrespondanteGamme {
  readonly id: string;
  readonly label: string | null;
  readonly model: string | null;
  readonly serialNumber: string | null;
  readonly siteName: string | null;
  /** Code de la gamme déjà rattachée à cette machine, le cas échéant. */
  readonly rattacheeA: string | null;
  /** Sa référence constructeur n'est pas celle de la gamme (ou n'est pas renseignée) : une information. */
  readonly avertissementReference: AvertissementReferenceGamme | null;
}

/** Créer un brouillon de version : copié d'une version existante (c'est le cas d'usage de l'écran). */
export interface CreerVersionInput {
  readonly validFrom: string;
  readonly source: string;
  readonly motif: string;
  readonly fromVersionId?: string;
}

/** Contenu à enregistrer d'un bloc dans un brouillon (forme validée côté écran, re-vérifiée par l'API). */
export interface RemplacerContenuInput {
  readonly contenu: unknown;
  readonly revision: number;
}

/** Un équivalent déjà associé à une référence officielle : texte et fournisseur, jamais un prix. */
export interface EquivalentConnu {
  readonly reference: string;
  readonly supplierPartyId: string;
  readonly supplierName: string;
}

/** Une désignation déjà utilisée pour une référence, avec ses sources (tarif, gammes). Aucun prix. */
export interface DesignationConnue {
  readonly designation: string;
  /** Date d'effet la plus récente parmi les sources de ce texte (YYYY-MM-DD). */
  readonly validFrom: string;
  /** Nombre de gammes distinctes qui la portent : le tarif n'en fait pas partie. */
  readonly nbGammes: number;
  /** Les premières de ces gammes (les plus récentes). */
  readonly gammes: readonly { readonly code: string; readonly label: string }[];
  /** Présent quand le TARIF du fabricant porte aussi ce texte (il passe d'abord) ; absent sinon. */
  readonly tarif?: { readonly label: string; readonly validFrom: string };
}

/** Une ligne du tarif du fabricant, proposée pour compléter une référence dont on ne connaît que le début. */
export interface ReferenceDuTarif {
  readonly reference: string;
  readonly description: string;
  /** `null` quand le fichier du fabricant n'a pas de colonne de famille. */
  readonly famille: string | null;
  /** Libellé du tarif qui la porte. */
  readonly tarif: string;
  /** Absent quand l'utilisateur ne voit pas les prix. */
  readonly prix?: number;
}

export interface ReferencesDuTarif {
  readonly items: readonly ReferenceDuTarif[];
  /** Combien de lignes correspondent en tout : `items` n'en montre que les premières. */
  readonly total: number;
  /** Tarifs consultés ; vide = le fabricant n'a aucun tarif en vigueur, rien à proposer. */
  readonly tarifs: readonly string[];
}

/** Un prix déjà connu pour une référence, avec ses sources : proposé, jamais posé d'office. */
export interface PrixConnu {
  readonly price: number;
  /** Date d'effet la plus récente parmi les sources de ce prix (YYYY-MM-DD). */
  readonly validFrom: string;
  /** Nombre de gammes distinctes qui le portent : le tarif n'en fait pas partie. */
  readonly nbGammes: number;
  /** Les premières de ces gammes (les plus récentes). */
  readonly gammes: readonly { readonly code: string; readonly label: string }[];
  /** Présent quand le TARIF du fabricant porte ce prix (il passe d'abord) ; absent sinon. */
  readonly tarif?: { readonly label: string; readonly validFrom: string };
}

// ------------------------------------------------------- tarifs des fabricants

// ----------------------------------------------- conditions d'achat (registre B26, lot P1)

export type TypeConditionAchat = 'DISCOUNT' | 'NET';
export type PorteeConditionAchat = 'FOURNISSEUR' | 'FAMILLE' | 'REFERENCE';

export interface ConditionAchat {
  readonly id: string;
  readonly partyId: string;
  readonly portee: PorteeConditionAchat;
  readonly famille: string | null;
  readonly reference: string | null;
  readonly kind: TypeConditionAchat;
  /** Remises en cascade en pourcentage, au point décimal (« 50 », « 10 ») ; vide pour un prix net. */
  readonly remises: readonly string[];
  /** La remise pondérée (« 55 »), ou `null` pour un prix net. */
  readonly remiseTotale: string | null;
  readonly coefficient: string;
  /** AAAA-MM-JJ. */
  readonly validFrom: string;
  readonly note: string | null;
  readonly createdAt: string;
  readonly archivedAt: string | null;
  /** C'est la condition qui s'applique aujourd'hui à sa portée. */
  readonly enVigueur: boolean;
  /** Date d'effet future : elle ne s'applique pas encore. */
  readonly aVenir: boolean;
}

export interface ConditionsDuTiers {
  readonly partyId: string;
  readonly partyName: string;
  readonly items: readonly ConditionAchat[];
}

export interface EntreeConditionAchat {
  readonly partyId: string;
  readonly famille?: string | null;
  readonly reference?: string | null;
  readonly kind: TypeConditionAchat;
  readonly remises: readonly string[];
  readonly coefficient: string;
  readonly validFrom: string;
  readonly note?: string | null;
}

export interface CreationConditionAchat {
  readonly condition: ConditionAchat;
  /** Accepté mais à relire (remise très forte, coefficient de 1 ou plus). */
  readonly avertissements: readonly string[];
}

export interface FamilleDuTarif {
  readonly cle: string;
  readonly famille: string;
  readonly graphies: readonly string[];
  readonly references: number;
}

export interface FamillesDuTarif {
  readonly version: { readonly id: string; readonly versionNumber: number; readonly validFrom: string } | null;
  readonly familles: readonly FamilleDuTarif[];
  readonly sansFamille: number;
}

export type RaisonSansPrixAchat =
  | 'AUCUNE_CONDITION'
  | 'FAMILLE_INCONNUE'
  | 'REFERENCE_AMBIGUE'
  | 'PRIX_TARIF_INUTILISABLE'
  | 'ACHAT_NUL'
  | 'MONTANT_INVRAISEMBLABLE'
  | 'AUCUN_PRIX_DE_TARIF';

export interface ResultatEssaiPrix {
  readonly reference: string;
  readonly jour: string;
  readonly tarif: {
    readonly tarif: string;
    readonly versionNumber: number;
    readonly reference: string;
    readonly description: string;
    readonly famille: string | null;
    readonly prix: number;
  } | null;
  readonly ambigue: boolean;
  readonly condition: {
    readonly id: string;
    readonly niveau: 'ARTICLE' | 'FAMILLE' | 'FOURNISSEUR';
    readonly kind: TypeConditionAchat;
    readonly remises: readonly string[];
    readonly remiseTotale: string | null;
    readonly coefficient: string;
    readonly validFrom: string;
  } | null;
  readonly prix: { readonly tarif: number; readonly achat: number; readonly vente: number } | null;
  readonly raison: RaisonSansPrixAchat | null;
}

// ----------------------------------------------- mise à jour des gammes d'après un tarif (lot C2)

export type CasDesignationTarif = 'identique' | 'complete' | 'reformule' | 'inutilisable';
export type CasPrixTarif = 'identique' | 'different' | 'manquant' | 'inutilisable';

export interface LigneDeGroupeTarif {
  readonly profileId: string;
  readonly gammeCode: string;
  readonly gammeLabel: string;
  readonly levelCode: string;
  readonly lineKey: string;
  readonly designation: string;
  readonly designationCas: CasDesignationTarif;
  /** ABSENTS (pas `null`) pour qui ne voit pas les prix. */
  readonly prix?: number | null;
  readonly prixCas?: CasPrixTarif;
  readonly variation?: number | null;
}

/** Une référence, toutes gammes confondues : une décision vaut pour toutes ses lignes. */
export interface GroupeReferenceTarif {
  readonly cle: string;
  /** La référence telle qu'écrite dans le tarif. */
  readonly reference: string;
  readonly description: string;
  readonly famille: string | null;
  /** Cas du groupe : le plus invasif de ses lignes (reformulée avant complétée avant identique). */
  readonly designation: CasDesignationTarif;
  readonly prixTarif?: number;
  readonly prix?: CasPrixTarif;
  readonly variationMax?: number | null;
  readonly lignes: readonly LigneDeGroupeTarif[];
}

export interface GammeARevoir {
  readonly profileId: string;
  readonly code: string;
  readonly label: string;
  /** La version en vigueur que la revue a montrée : celle dont le brouillon sera la copie. */
  readonly versionId: string;
  readonly versionNumber: number;
  readonly validFrom: string;
  readonly nbLignes: number;
}

export type RaisonGammeIgnoree = 'SANS_VERSION' | 'BROUILLON_OUVERT' | 'VERSION_A_VENIR' | 'DEJA_TRAITEE';

export interface GammeIgnoreeTarif {
  readonly profileId: string;
  readonly code: string;
  readonly label: string;
  readonly raison: RaisonGammeIgnoree;
  readonly detail: string;
}

export interface BrouillonEnAttenteTarif {
  readonly profileId: string;
  readonly code: string;
  readonly label: string;
  readonly versionId: string;
  readonly versionNumber: number;
  readonly sourceVersionNumber: number | null;
  /** La version en vigueur de la gamme n'est plus la source : le publier effacerait ce qui a changé depuis. */
  readonly perimee: boolean;
  /** Date d'effet du brouillon, `YYYY-MM-DD` (elle a pu être retouchée depuis la création). */
  readonly validFrom: string;
  /** La version en vigueur de la gamme aujourd'hui, ce que la publication remplacerait ; `null` : aucune. */
  readonly enVigueur: { readonly versionNumber: number; readonly validFrom: string } | null;
}

export interface ReferenceSansPropositionTarif {
  readonly cle: string;
  readonly reference: string;
  readonly nbLignes: number;
}

export interface RevueTarifGammes {
  readonly tarif: {
    readonly id: string;
    readonly label: string;
    readonly versionId: string;
    readonly versionNumber: number;
    readonly validFrom: string;
    readonly aVenir: boolean;
    readonly fileName: string;
  };
  readonly marqueur: string;
  readonly dateEffetProposee: string;
  readonly avecPrix: boolean;
  readonly groupes: readonly GroupeReferenceTarif[];
  readonly gammes: readonly GammeARevoir[];
  readonly ignorees: readonly GammeIgnoreeTarif[];
  readonly horsTarif: readonly ReferenceSansPropositionTarif[];
  readonly ambigues: readonly ReferenceSansPropositionTarif[];
  readonly brouillonsEnAttente: readonly BrouillonEnAttenteTarif[];
}

export interface DecisionTarif {
  readonly cle: string;
  readonly designation: boolean;
  readonly prix: boolean;
}

export interface AppliquerTarifGammesInput {
  readonly decisions: readonly DecisionTarif[];
  /** Pour chaque gamme à traiter, la version en vigueur que la revue a montrée. */
  readonly gammes: readonly { readonly profileId: string; readonly versionId: string }[];
  /** Date d'effet des brouillons, `YYYY-MM-DD`. */
  readonly validFrom: string;
}

export type CodeRefusGamme =
  | 'INTROUVABLE'
  | 'ARCHIVEE'
  | 'SANS_VERSION'
  | 'A_CHANGE'
  | 'BROUILLON_OUVERT'
  | 'VERSION_A_VENIR'
  | 'DATE_ANTERIEURE'
  | 'AUCUN_CHANGEMENT'
  | 'CONTENU_INVALIDE'
  | 'FOURNISSEUR_ARCHIVE'
  | 'CONFLIT'
  | 'ERREUR';

export interface BilanGammeTarif {
  readonly profileId: string;
  readonly code: string;
  readonly label: string;
  readonly brouillon: { readonly id: string; readonly versionNumber: number } | null;
  readonly lignes: number;
  readonly designations: number;
  /** ABSENT (pas `null`) pour qui ne voit pas les prix. */
  readonly prix?: number;
  readonly refus?: { readonly code: CodeRefusGamme; readonly message: string };
}

export interface BilanApplicationTarif {
  readonly marqueur: string;
  readonly validFrom: string;
  readonly gammes: readonly BilanGammeTarif[];
  readonly brouillons: number;
  readonly refus: number;
  readonly lignes: number;
  readonly designations: number;
  readonly prix?: number;
}

// ------------------------------------------------------- recalcul des prix d'une marque (lot P2)

export interface DecompteCasPrix {
  /** Un prix qui apparaît (jamais calculé jusqu'ici). */
  readonly nouveaux: number;
  readonly changent: number;
  /** Un prix stocké qui ne se calcule plus (condition archivée, tarif changé…). */
  readonly perdus: number;
  readonly identiques: number;
  readonly nonCalcules: number;
}

export interface ResumeGammeRecalcul {
  readonly nbLignes: number;
  readonly nbSansPrixTarif: number;
  readonly vente: DecompteCasPrix;
  readonly achat: DecompteCasPrix;
  /** Un recalcul changerait au moins un prix : cela vaut la peine d'ouvrir un brouillon. */
  readonly aChanger: boolean;
}

export interface GammeARecalculer {
  readonly profileId: string;
  readonly code: string;
  readonly label: string;
  /** La version en vigueur au moment de la revue : celle dont le brouillon sera la copie. */
  readonly versionId: string;
  readonly versionNumber: number;
  readonly validFrom: string;
  readonly resume: ResumeGammeRecalcul;
}

export type RaisonSansPrixLigne =
  | 'AUCUNE_CONDITION'
  | 'FAMILLE_INCONNUE'
  | 'REFERENCE_AMBIGUE'
  | 'PRIX_TARIF_INUTILISABLE'
  | 'ACHAT_NUL'
  | 'MONTANT_INVRAISEMBLABLE'
  | 'SANS_PRIX_DU_TARIF'
  | 'SANS_PRIX_EQUIVALENT'
  | 'CONDITION_ILLISIBLE';

/** Un prix absent, et pourquoi : regroupé par chaîne, raison et famille, pour dire QUOI saisir. */
export interface ManqueDeConditionPrix {
  /** `VENTE` : le prix montré au client ; `ACHAT` : le coût réel d'un équivalent. */
  readonly chaine: 'VENTE' | 'ACHAT';
  readonly raison: RaisonSansPrixLigne;
  readonly famille: string | null;
  readonly nbLignes: number;
  readonly nbGammes: number;
  readonly references: readonly string[];
}

export interface RevueRecalculPrix {
  readonly marque: { readonly partyId: string; readonly nom: string };
  /** Le jour des conditions retenues : aujourd'hui. */
  readonly jour: string;
  readonly dateEffetProposee: string;
  readonly marqueur: string;
  /** Conditions d'achat en cours de la marque : 0 = rien à calculer, il faut d'abord en saisir. */
  readonly nbConditions: number;
  readonly gammes: readonly GammeARecalculer[];
  readonly ignorees: readonly GammeIgnoreeTarif[];
  readonly manques: readonly ManqueDeConditionPrix[];
  readonly brouillonsEnAttente: readonly BrouillonEnAttenteTarif[];
}

export interface AppliquerRecalculPrixInput {
  /** Pour chaque gamme à traiter, la version en vigueur que la revue a montrée. */
  readonly gammes: readonly { readonly profileId: string; readonly versionId: string }[];
  /** Date d'effet des brouillons, `YYYY-MM-DD`. */
  readonly validFrom: string;
}

export interface BilanGammeRecalculPrix {
  readonly profileId: string;
  readonly code: string;
  readonly label: string;
  readonly brouillon: { readonly id: string; readonly versionNumber: number } | null;
  /** Ce que le brouillon, une fois publié, changera (présent quand le brouillon est créé). */
  readonly resume?: ResumeGammeRecalcul;
  readonly refus?: { readonly code: CodeRefusGamme; readonly message: string };
}

export interface BilanRecalculPrix {
  readonly marqueur: string;
  readonly validFrom: string;
  readonly gammes: readonly BilanGammeRecalculPrix[];
  readonly brouillons: number;
  readonly refus: number;
}

export interface VersionTarif {
  readonly id: string;
  readonly versionNumber: number;
  /** YYYY-MM-DD */
  readonly validFrom: string;
  readonly fileName: string;
  readonly source: string;
  readonly lineCount: number;
  readonly createdAt: string;
  readonly archivedAt: string | null;
  /** Vraie pour la seule version qui s'applique aujourd'hui. */
  readonly enVigueur: boolean;
}

export interface TarifResume {
  readonly id: string;
  readonly partyId: string;
  readonly partyName: string;
  readonly label: string;
  readonly archivedAt: string | null;
  readonly versionEnVigueur: VersionTarif | null;
  readonly nbVersions: number;
}

export interface TarifDetail extends TarifResume {
  readonly versions: readonly VersionTarif[];
}

export interface LigneTarifRecherchee {
  readonly reference: string;
  readonly description: string;
  readonly famille: string | null;
  /** ABSENT (pas `null`) pour qui ne voit pas les prix. */
  readonly prix?: number;
}

export interface ResultatRechercheTarif {
  readonly items: readonly LigneTarifRecherchee[];
  readonly total: number;
  readonly version: Pick<VersionTarif, 'id' | 'versionNumber' | 'validFrom' | 'lineCount'> | null;
}

export interface VariationPrixTarif {
  readonly reference: string;
  readonly description: string;
  readonly ancien: number;
  readonly nouveau: number;
  readonly ecart: number;
  readonly variation: number | null;
}

export interface EcartsTarif {
  readonly nouvelles: number;
  readonly disparues: number;
  readonly hausses: number;
  readonly baisses: number;
  readonly inchangees: number;
  readonly descriptionsModifiees: number;
  readonly plusFortesHausses: readonly VariationPrixTarif[];
  readonly plusFortesBaisses: readonly VariationPrixTarif[];
}

export interface ApercuImportTarif {
  readonly nomFichier: string;
  readonly sha256: string;
  readonly feuille: string;
  readonly entetes: {
    readonly reference: string;
    readonly description: string;
    readonly famille: string | null;
    readonly prix: string;
  };
  readonly nbLignes: number;
  readonly erreurs: readonly { readonly ligne: number | null; readonly message: string }[];
  readonly nbErreurs: number;
  readonly avertissements: readonly string[];
  /** YYYY-MM-DD, lu dans le classeur ; `null` si rien n'a été trouvé. */
  readonly validFromDetectee: string | null;
  readonly versionEnVigueur: { readonly versionNumber: number; readonly validFrom: string; readonly lineCount: number } | null;
  readonly ecarts: EcartsTarif | null;
  readonly dejaImporte: { readonly versionNumber: number; readonly validFrom: string } | null;
  readonly alertes: readonly string[];
  readonly publiable: boolean;
}

/** Informations d'un brouillon : un champ absent n'est pas touché. `revision` est obligatoire. */
export interface ModifierVersionInput {
  readonly validFrom?: string;
  readonly source?: string;
  readonly motif?: string;
  readonly revision: number;
}

export interface GammeDetail extends GammeResume {
  readonly versions: readonly GammeVersion[];
  readonly machinesCorrespondantes: readonly MachineCorrespondanteGamme[];
}

/** Rattachement explicite d'une machine à une gamme — jamais automatique. */
/**
 * La référence constructeur d'une machine n'est pas celle pour laquelle une gamme a été
 * établie. Une information, jamais un refus : la décision reste à l'utilisateur.
 * `DIFFERENTE` : une autre référence ; `ABSENTE` : la machine n'en renseigne pas.
 */
export interface AvertissementReferenceGamme {
  readonly type: 'DIFFERENTE' | 'ABSENTE';
  readonly referencesGamme: readonly string[];
  readonly referenceMachine: string | null;
}

export interface RattachementGamme {
  readonly id: string;
  readonly assignedAt: string;
  readonly assignedBy: string | null;
  readonly profile: { readonly id: string; readonly code: string; readonly label: string };
  /** Version publiée en vigueur aujourd'hui ; `null` si la gamme n'en a plus. */
  readonly version: GammeVersion | null;
  readonly avertissementReference: AvertissementReferenceGamme | null;
}

/** Gamme proposée pour une machine : une suggestion, jamais une décision. */
export interface SuggestionGamme {
  readonly profileId: string;
  readonly code: string;
  readonly label: string;
  readonly scope: 'SHARED' | 'INDIVIDUAL';
  readonly versionNumber: number;
  readonly validFrom: string;
  readonly avertissementReference: AvertissementReferenceGamme | null;
}

export interface MaintenanceMachine {
  readonly rattachement: RattachementGamme | null;
  readonly suggestions: readonly SuggestionGamme[];
  /** Lot T4 : pourquoi aucune gamme n'est proposée (machine à valider). */
  readonly raison?: string;
}

// --- Bons d'intervention saisis au bureau (lot I1.1, ADR-0014) ----------------------
//
// Un bon n'a AUCUN prix ni total : aucune donnée financière dans ces types (ADR-0011).

/** Les 14 états du CDC ; un bon saisi au bureau n'en parcourt que quatre : DRAFT, TO_REVIEW, REVIEWED, CANCELLED. */
export type EtatBon =
  | 'DRAFT'
  | 'TO_ASSIGN'
  | 'PLANNED'
  | 'DISPATCHED'
  | 'IN_PROGRESS'
  | 'COMPLETED_BY_TECH'
  | 'TO_REVIEW'
  | 'REVIEWED'
  | 'TRANSMITTED'
  | 'CLASSIFIED'
  | 'TO_QUOTE'
  | 'TO_INVOICE'
  | 'CLOSED'
  | 'CANCELLED';

/** Une pièce d'une gamme versée dans un bon, à contrôler au tarif. */
export interface LigneAVerifierBon {
  readonly reference: string;
  readonly designation: string;
}

/** Une ligne du tarif proposée à la place d'une référence qui n'y est plus. */
export interface CandidatReferenceBon {
  readonly reference: string;
  readonly description: string;
}

/**
 * `AU_TARIF` : rien à faire ; `REMPLACEE` : plus au tarif, UNE seule référence du tarif commence pareil avec la même
 * désignation (remplacement sûr) ; `A_CHOISIR` : plus au tarif, remplacement pas sûr (candidats à choisir, parfois aucun) ;
 * `TARIF_NON_CHARGE` : le fabricant n'a aucun tarif en vigueur, la référence n'est pas contrôlée.
 */
export type EtatReferenceBon = 'AU_TARIF' | 'REMPLACEE' | 'A_CHOISIR' | 'TARIF_NON_CHARGE';

export interface ReferenceVerifieeBon {
  readonly etat: EtatReferenceBon;
  readonly remplacement: CandidatReferenceBon | null;
  readonly candidats: readonly CandidatReferenceBon[];
}

export interface ReferencesVerifieesBon {
  readonly items: readonly ReferenceVerifieeBon[];
  readonly tarifs: readonly string[];
}

/** Les types de ligne : pièce, main d'œuvre, déplacement, forfait, plus le matériel (livraison, installation) et l'accessoire (location). */
export type GenreLigneBon = 'PART' | 'LABOR' | 'TRAVEL' | 'FLAT_FEE' | 'EQUIPMENT' | 'ACCESSORY';

/** Les modèles de feuille d'un bon (ADR-0014 §14) : une nature de bon en porte un. */
export type ModeleBon = 'MAINTENANCE' | 'REPAIR' | 'INSTALLATION' | 'DELIVERY' | 'RENTAL';

/** Sous contrat ou facturé : l'en-tête du bon qui le classe (forfait) ou l'envoie en facturation (réel). */
export type FacturationBon = 'CONTRACT' | 'BILLABLE';

/** Le dossier technique d'une installation : remis en main propre, ou laissé dans l'armoire électrique. */
export type ChoixDossier = 'HANDED' | 'IN_CABINET';

/** Les six suites à donner de la conception §5.9. */
export type GenreSuiteBon =
  | 'QUOTE_TO_DO'
  | 'REVISION_TO_PLAN'
  | 'PART_TO_ORDER'
  | 'RETURN_TO_PLAN'
  | 'WATCH_MACHINE'
  | 'REGULATORY_CHECK';

export interface ReleveBon {
  /** Le compteur de la MACHINE sur lequel la valeur est lue. */
  readonly counterId: string;
  /** Chaîne décimale (point) ; vide tant qu'elle n'est pas saisie. */
  readonly value: string;
}

export interface LigneBon {
  /** Identité stable de la ligne dans le brouillon (UUID). */
  readonly lineKey: string;
  readonly kind: GenreLigneBon;
  readonly reference: string | null;
  readonly designation: string;
  /** Chaîne décimale ; `null` tant qu'elle n'est pas saisie. */
  readonly quantity: string | null;
  readonly unit: string | null;
  readonly note: string | null;
  /** Le n° de série d'un matériel livré ou installé (ligne de type « matériel ») ; `null` pour toute autre ligne. */
  readonly serialNumber: string | null;
  /** La gamme et le palier d'où le bouton « Ajouter les pièces du palier » a versé cette ligne ; absent pour une ligne saisie à la main. */
  readonly versedFrom?: { readonly profileId: string; readonly levelCode: string };
}

export interface SuiteBon {
  readonly followUpKey: string;
  readonly kind: GenreSuiteBon;
  readonly comment: string;
}

/** Un contrôle de la nature du bon, fait ou non : le libellé est l'instantané de la liste de la nature à la saisie. */
export interface ControleBon {
  /** L'identité stable du contrôle dans la liste de la nature (UUID). */
  readonly checkKey: string;
  readonly label: string;
  readonly checked: boolean;
}

/** Les champs propres au modèle de feuille : problème signalé, travaux réalisés, réserves, dossier remis, dates de location. */
export interface DetailsBon {
  readonly reportedProblem: string;
  readonly workDone: string;
  readonly reservations: string;
  readonly dossier: ChoixDossier | null;
  /** Mise à disposition (location), AAAA-MM-JJ. */
  readonly rentalFrom: string | null;
  /** Retour prévu (location), AAAA-MM-JJ. */
  readonly rentalReturn: string | null;
}

export const DETAILS_BON_VIDES: DetailsBon = {
  reportedProblem: '',
  workDone: '',
  reservations: '',
  dossier: null,
  rentalFrom: null,
  rentalReturn: null,
};

export interface ContenuBon {
  readonly readings: readonly ReleveBon[];
  readonly lines: readonly LigneBon[];
  /** L'identité stable du palier de gamme réalisé ; `null` = aucun. */
  readonly levelKey: string | null;
  readonly followUps: readonly SuiteBon[];
  /** Les techniciens de l'intervention, par leur nom : une intervention n'est pas toujours faite seul. */
  readonly technicians: readonly string[];
  /** Les contrôles de la nature du bon, dans l'ordre d'impression, avec ce qui a été fait. Vide = aucun contrôle. */
  readonly checks: readonly ControleBon[];
  /** Les champs propres au modèle de feuille (texte libre, dossier remis, dates de location). */
  readonly details: DetailsBon;
}

export interface BonResume {
  readonly id: string;
  /** Numéro à nous : BI-AAAA-NNNNN. */
  readonly number: string;
  readonly praxedoNumber: string | null;
  readonly status: EtatBon;
  readonly origin: 'DESK_ENTRY' | 'PLANNED';
  /** La nature du bon, avec son MODÈLE de feuille : ce que l'écran propose et ce que le papier imprime. */
  readonly nature: {
    readonly id: string;
    readonly code: string;
    readonly label: string;
    readonly layout: ModeleBon;
    /** Les valeurs standard de la feuille (températures, pression, tension…) : imprimées avec un « — » quand elles sont vides. */
    readonly valeursStandard: readonly ValeurStandardNature[];
  };
  /** La machine du bon ; `null` pour une livraison faite sur un site seul. */
  readonly asset: {
    readonly id: string;
    readonly label: string | null;
    readonly model: string | null;
    readonly serialNumber: string | null;
    readonly clientDesignation: string | null;
    readonly archived: boolean;
  } | null;
  readonly site: { readonly id: string; readonly name: string } | null;
  readonly customer: { readonly id: string; readonly name: string } | null;
  /** Sous contrat ou facturé ; `null` tant qu'il n'est pas choisi (jamais présélectionné). */
  readonly billing: FacturationBon | null;
  readonly technicianLabel: string | null;
  /** AAAA-MM-JJ. */
  readonly performedOn: string | null;
  /** Le premier jour travaillé prévu au planning (AAAA-MM-JJ), rendu par la LISTE seulement ; `null` sans jour. */
  readonly premierJour?: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** Jeton de concurrence : à renvoyer à chaque écriture. */
  readonly revision: number;
}

/** Le site d'un bon lu en détail : son nom et son adresse postale (pour le papier) ; rien d'autre de sa fiche. */
export interface SiteDuBon {
  readonly id: string;
  readonly name: string;
  readonly addressLine1: string | null;
  readonly addressLine2: string | null;
  readonly postalCode: string | null;
  readonly city: string | null;
}

/**
 * Ce que le planning a prévu pour l'intervention : ses jours travaillés (AAAA-MM-JJ, dans l'ordre) et les noms de l'équipe
 * (le chef d'abord). Vides pour un bon saisi après coup et pour un bon validé. Sert à PRÉREMPLIR le bon.
 */
export interface BonPlanification {
  readonly jours: readonly string[];
  readonly equipe: readonly string[];
  /** Les heures SAISIES de l'équipe, par jour et par technicien (la même source que le planning). */
  readonly heures: readonly HeuresEquipeBon[];
}

/** Une ligne d'heures de l'équipe : le technicien est un nom de l'annuaire, comme les techniciens du bon. */
export interface HeuresEquipeBon {
  readonly jour: string;
  readonly technicien: string;
  readonly minutes: number;
  /** Les minutes FACTURÉES (palier supérieur), écrites à la validation ; `null` tant que le bon n'est pas validé. */
  readonly minutesFacturees: number | null;
  /** Registre B34 : heures CALCULÉES d'après les pointages du technicien ce jour-là (lecture seule : on corrige un pointage). */
  readonly pointe: boolean;
}

// --- Les pointages des techniciens (registre B34) -----------------------------------------------------------------------

export type GestePointage = 'DEPART' | 'ARRIVAL' | 'PAUSE' | 'RESUME' | 'LEAVE' | 'HOME';
export type DestinationPointage = 'NEXT_CLIENT' | 'WORKSHOP' | 'HOME';
export type EtatJourneePointage = 'AVANT' | 'EN_ROUTE' | 'SUR_SITE' | 'EN_PAUSE' | 'PARTI' | 'RETOUR' | 'RENTRE';

export interface EvenementPointage {
  readonly id: string;
  readonly geste: GestePointage;
  readonly destination: DestinationPointage | null;
  readonly travailFini: boolean | null;
  readonly occurredAt: string;
  readonly saisieApresCoup: boolean;
  readonly source: 'FIELD' | 'DESK';
}

export interface DureesPointage {
  readonly trajetAller: number;
  readonly surSite: number;
  readonly pause: number;
  readonly trajetRetour: number;
}

/** Les pointages d'un technicien un jour donné, avec les durées calculées et la main d'œuvre facturée (minutes). */
export interface PointagesJour {
  readonly jour: string;
  readonly technicien: { readonly id: string; readonly nom: string };
  readonly etat: EtatJourneePointage;
  readonly durees: DureesPointage;
  readonly minutesFacturees: number;
  readonly evenements: readonly EvenementPointage[];
  readonly saisiesApresCoup: number;
  readonly anomalies: readonly string[];
}

export interface PointagesBureau {
  readonly revision: number;
  readonly pointages: readonly PointagesJour[];
}

export interface AjoutPointage {
  readonly jour: string;
  readonly technicienId: string;
  readonly geste: GestePointage;
  readonly occurredAt: string;
  readonly destination?: DestinationPointage | null;
  readonly travailFini?: boolean | null;
}

export interface BonDetail extends Omit<BonResume, 'site'> {
  readonly planification: BonPlanification;
  /** Registre B34 : les pointages par jour et par technicien (seulement ceux qui ont pointé). */
  readonly pointages: readonly PointagesJour[];
  readonly site: SiteDuBon | null;
  readonly contactLabel: string | null;
  readonly observations: string | null;
  /** Les champs propres au modèle : ceux du brouillon, puis ceux que la validation a écrits. Toujours présents. */
  readonly details: DetailsBon;
  /** Le brouillon ; `null` une fois le bon validé. */
  readonly content: ContenuBon | null;
  /** Ce que la validation a écrit (le brouillon est alors vidé) ; `null` tant que le bon n'est pas validé. */
  readonly validated: BonValide | null;
  readonly cancelReason: string | null;
  readonly cancelledAt: string | null;
  readonly reviewedAt: string | null;
  /** La clôture faite sur le téléphone par le chef du jour (lot T3) ; `null` sinon. */
  readonly cloture: ClotureBon | null;
}

/** Le client a signé (nom, instant, image) ou il était absent (motif) ; la signature du chef pour l’équipe. */
export interface ClotureBon {
  readonly closedAt: string;
  readonly signerName: string | null;
  readonly signedAt: string | null;
  readonly absentReason: string | null;
  readonly clientSignatureFileId: string | null;
  readonly technicianSignatureFileId: string | null;
}

export interface ListeBons {
  readonly items: readonly BonResume[];
  readonly total: number;
}

export interface FiltreBons {
  readonly q?: string;
  readonly statuses?: string;
  readonly natureId?: string;
  readonly assetId?: string;
  readonly siteId?: string;
  readonly customerId?: string;
  /** Sous contrat (`CONTRACT`) ou facturé (`BILLABLE`). */
  readonly billing?: FacturationBon;
  readonly from?: string;
  readonly to?: string;
  readonly take?: number;
  readonly skip?: number;
}

/** Un nom proposé dans le menu « Technicien » : l'annuaire des noms, plus les comptes au rôle Technicien. */
export interface TechnicienBon {
  readonly name: string;
}

/** Les champs d'en-tête qu'on modifie en brouillon. Absent = inchangé, `null` = vider. */
export interface EnteteBon {
  readonly praxedoNumber?: string | null;
  readonly contactLabel?: string | null;
  /** AAAA-MM-JJ. */
  readonly performedOn?: string | null;
  readonly observations?: string | null;
  /** Sous contrat ou facturé ; `null` le vide. */
  readonly billing?: FacturationBon | null;
  readonly content?: ContenuBon;
}

export interface EntreeCreationBon extends EnteteBon {
  readonly natureId: string;
  /** La machine du bon : obligatoire sauf pour une livraison, qui peut se faire sur un site seul (`siteId`). */
  readonly assetId?: string;
  readonly siteId?: string;
  readonly clientKey?: string;
}

export interface EntreeSauvegardeBon extends EnteteBon {
  readonly revision: number;
  /** Les heures de l'équipe écrites avec le bon ; `minutes` nul efface. Absent = rien n'est touché. */
  readonly heures?: readonly { readonly jour: string; readonly technicien: string; readonly minutes: number | null }[];
  readonly natureId?: string;
}

/** Un élément de la liste de contrôles d'une nature (« Courroies / accouplement »), avec son identité stable. */
export interface ElementControleNature {
  readonly key: string;
  readonly label: string;
}

/** Une valeur que la feuille d'un modèle de bon prévoit de relever en plus des heures : un compteur de la machine, créé à la saisie. */
export interface ValeurStandardNature {
  /** Identité stable (`TEMP_AMBIENT`…) : c'est elle qui voyage vers le serveur, jamais le libellé. */
  readonly key: string;
  readonly label: string;
  readonly unit: string;
  /** La feuille l'imprime sur la ligne des heures, à leur suite (la pression de service d'une mise en service). */
  readonly avecLesHeures?: boolean;
}

/** Un compteur standard prêt à recevoir un relevé : créé à l'instant, ou déjà sur la machine. */
export interface CompteurStandardCree {
  readonly key: string;
  readonly counterId: string;
  readonly label: string;
  readonly unit: string | null;
  readonly created: boolean;
}

export interface NatureBon {
  readonly id: string;
  readonly code: string;
  readonly label: string;
  /** Le modèle de feuille : ce que l'écran propose et ce que le papier imprime. Ne change plus dès qu'un bon porte la nature. */
  readonly layout: ModeleBon;
  /** Cette nature peut enregistrer un entretien réalisé à la validation. */
  readonly recordsService: boolean;
  readonly sortOrder: number;
  /** Les contrôles de cette nature, dans l'ordre d'impression du bon. */
  readonly checkItems: readonly ElementControleNature[];
  /** Les valeurs standard du modèle de feuille de cette nature (vide : la livraison ne relève rien). */
  readonly valeursStandard: readonly ValeurStandardNature[];
  readonly archived: boolean;
  readonly revision: number;
}

// --- « Préparer l’année » (lot U5, ADR-0016) ---------------------------------------------------------------

export interface PropositionAnnee {
  readonly obligationId: string;
  readonly etat: 'A_CREER' | 'DEJA_PLANIFIEE' | 'A_COMPLETER';
  readonly raison: 'MACHINE_SANS_SITE' | 'MACHINE_ARCHIVEE' | null;
  readonly echeance: string;
  /** Jours de retard (positif) ; nul ou négatif : pas encore dépassée. */
  readonly retardJours: number;
  readonly regle: string;
  readonly jalon: string;
  readonly machine: { readonly id: string; readonly libelle: string | null; readonly modele: string | null; readonly numeroSerie: string | null; readonly designationClient: string | null };
  readonly site: { readonly id: string; readonly nom: string; readonly ville: string | null } | null;
  readonly client: string | null;
  readonly organisme: string | null;
  readonly intervention: { readonly id: string; readonly numero: string | null; readonly statut: string } | null;
  readonly fenetre: { readonly debut: string; readonly fin: string };
}

export interface PreparationAnnee {
  readonly aujourdhui: string;
  readonly du: string;
  readonly au: string;
  readonly mois: number;
  readonly propositions: readonly PropositionAnnee[];
  readonly compte: { readonly aCreer: number; readonly dejaPlanifiees: number; readonly aCompleter: number };
  /** Les entretiens d'après les gammes (2026-10-30) ; absents d'une réponse plus ancienne. */
  readonly entretiens?: readonly PropositionEntretienAnnee[];
  readonly compteEntretiens?: { readonly aCreer: number; readonly dejaPlanifiees: number; readonly aCompleter: number };
  /** Ce que cette revue ne propose PAS encore, dit sans détour. */
  readonly nonCouvert: readonly string[];
}

/** Une proposition d'entretien : un point au compteur, une échéance au calendrier, un premier entretien, ou « à compléter ». */
export interface PropositionEntretienAnnee {
  /** La clé de rejeu : c'est elle qu'on coche. */
  readonly id: string;
  readonly etat: 'A_CREER' | 'DEJA_PLANIFIEE' | 'A_COMPLETER';
  readonly genre: 'RETARD' | 'POINT' | 'ECHEANCE' | 'PREMIER' | 'SANS_GAMME' | null;
  readonly palier: { readonly code: string; readonly libelle: string | null; readonly levelKey: string | null } | null;
  readonly cible: number | null;
  readonly date: string | null;
  readonly retardJours: number | null;
  readonly fenetre: { readonly debut: string; readonly fin: string } | null;
  readonly message: string | null;
  /** Nul pour une machine sans gamme d'entretien. */
  readonly mode: 'COUNTER' | 'CALENDAR' | null;
  readonly gamme: { readonly id: string; readonly code: string; readonly label: string } | null;
  readonly machine: {
    readonly id: string;
    readonly libelle: string | null;
    readonly modele: string | null;
    readonly numeroSerie: string | null;
    readonly designationClient: string | null;
    readonly famille: string;
  };
  readonly site: { readonly id: string; readonly nom: string; readonly ville: string | null } | null;
  readonly client: string | null;
  readonly intervention: { readonly id: string; readonly numero: string | null; readonly statut: string } | null;
}

export interface BilanPreparationAnnee {
  readonly creees: readonly { readonly obligationId: string; readonly interventionId: string; readonly numero: string | null }[];
  readonly refusees: readonly { readonly obligationId: string; readonly raison: string }[];
}

/** La base du calcul d’une machine sous contrat : les heures vendues, ou les relevés (les heures du contrat à défaut). */
export type BaseDuCalcul = 'CONTRACT' | 'READINGS';

export interface MachineContrat {
  readonly id: string;
  readonly libelle: string | null;
  readonly modele: string | null;
  readonly numeroSerie: string | null;
  readonly designationClient: string | null;
  readonly famille: string;
  readonly siteId: string;
  readonly siteNom: string;
  readonly gamme: { readonly id: string; readonly code: string; readonly label: string } | null;
  /** Le contrat EN COURS qui la couvre (au plus un). */
  readonly contrat: { readonly id: string; readonly label: string } | null;
}

export interface ContratVue {
  readonly id: string;
  readonly revision: number;
  readonly label: string;
  readonly signedOn: string;
  readonly endedOn: string | null;
  readonly controlEveryMonths: number | null;
  readonly siteId: string;
  readonly termine: boolean;
  readonly machines: readonly { readonly assetId: string; readonly hoursPerYear: number | null; readonly basis: BaseDuCalcul }[];
}

export interface ContratsDuSite {
  readonly site: { readonly id: string; readonly nom: string; readonly ville: string | null; readonly client: string | null };
  readonly machines: readonly MachineContrat[];
  readonly machinesDuClient: readonly MachineContrat[];
  readonly machinesDuClientTronquee: boolean;
  readonly contrats: readonly ContratVue[];
}

export interface EntreeContrat {
  readonly revision?: number | null;
  readonly label: string;
  readonly signedOn: string;
  readonly controlEveryMonths: number | null;
  readonly machines: readonly { readonly assetId: string; readonly hoursPerYear: number | null; readonly basis: BaseDuCalcul }[];
}

export interface InterventionLiee {
  readonly id: string;
  readonly numero: string | null;
  readonly statut: string;
  readonly premierJour: string | null;
  readonly fenetre: { readonly debut: string; readonly fin: string } | null;
  /** Une intervention planifiée dont l’échéance a bougé d’au moins 7 jours : de combien (positif = plus tard). */
  readonly decalageJours: number | null;
  /** Créée par la préparation pour cette ligne : elle se recale seule tant qu’elle est « à affecter ». */
  readonly creeeIci: boolean;
}

export interface LigneEntretienSite {
  readonly id: string;
  readonly etat: 'A_CREER' | 'DEJA_PLANIFIEE' | 'A_COMPLETER';
  readonly genre: 'RETARD' | 'POINT' | 'ECHEANCE' | 'PREMIER' | 'DUE' | null;
  readonly palier: { readonly code: string; readonly libelle: string | null; readonly levelKey: string | null } | null;
  readonly palierPropose: { readonly code: string; readonly libelle: string | null; readonly levelKey: string | null } | null;
  readonly auDelaDuContrat: boolean;
  readonly cible: number | null;
  readonly date: string | null;
  readonly retardJours: number | null;
  readonly fenetre: { readonly debut: string; readonly fin: string } | null;
  readonly message: string | null;
  readonly intervention: InterventionLiee | null;
}

export interface MachinePreparee {
  readonly machine: {
    readonly id: string;
    readonly libelle: string | null;
    readonly modele: string | null;
    readonly numeroSerie: string | null;
    readonly designationClient: string | null;
    readonly famille: string;
  };
  readonly gamme: { readonly id: string; readonly code: string; readonly label: string } | null;
  readonly mode: 'COUNTER' | 'CALENDAR' | null;
  readonly couverte: boolean;
  readonly contrat: { readonly id: string; readonly label: string } | null;
  readonly annee: { readonly debut: string; readonly fin: string };
  readonly heuresParAn: number | null;
  readonly base: BaseDuCalcul;
  readonly regime: { readonly heuresParAn: number; readonly origine: string } | null;
  readonly regimeReleve: number | null;
  readonly dues: number | null;
  readonly atteintes: number | null;
  readonly faites: number;
  readonly ecart: string | null;
  readonly lignes: readonly LigneEntretienSite[];
  readonly message: string | null;
}

export interface ControlePropose {
  readonly id: string;
  readonly contratId: string;
  readonly contratLibelle: string;
  readonly assetIds: readonly string[];
  readonly debut: string;
  readonly fin: string;
  readonly fenetre: { readonly debut: string; readonly fin: string };
  readonly etat: 'A_CREER' | 'DEJA_PLANIFIEE';
  readonly machines: number;
  readonly intervention: InterventionLiee | null;
}

export interface PreparationSite {
  readonly aujourdhui: string;
  readonly site: ContratsDuSite['site'];
  readonly contrats: readonly {
    readonly id: string;
    readonly label: string;
    readonly signedOn: string;
    readonly controlEveryMonths: number | null;
    readonly annee: { readonly debut: string; readonly fin: string };
    readonly machinesDuSite: number;
  }[];
  readonly annee: { readonly debut: string; readonly fin: string; readonly decalage: 0 | 1 };
  readonly machines: readonly MachinePreparee[];
  readonly controles: readonly ControlePropose[];
  readonly echeances: readonly PropositionAnnee[];
}

export interface CreationSite {
  readonly decalage: 0 | 1;
  readonly natureId: string;
  readonly natureControleId: string | null;
  readonly entretiens: readonly { readonly id: string; readonly choix: 'AVANCER' | 'SANS_PALIER' | null }[];
  readonly controles: readonly string[];
  readonly obligationIds: readonly string[];
}

export interface BilanCreationSite {
  readonly creees: readonly { readonly id: string; readonly interventionId: string; readonly numero: string | null }[];
  readonly refusees: readonly { readonly id: string; readonly raison: string }[];
}


export interface BilanSemisNatures {
  readonly creees: readonly string[];
  readonly dejaPresentes: readonly string[];
  /** Les natures dont la liste de contrôles, vide, a reçu ses contrôles de départ. */
  readonly controlesInstalles: readonly string[];
  /** Les natures de départ restées sur le modèle « Entretien » (sans bon) qui ont pris leur modèle de feuille. */
  readonly modelesInstalles: readonly string[];
}

// --- La validation d'un bon (lot I1.2) -----------------------------------------------

export type EtatSuiteBon = 'OPEN' | 'DISMISSED' | 'CLOSED' | 'QUALIFIED' | 'CONVERTED';

/** Une ligne d'un bon VALIDÉ. Aucun prix, aucun total. */
export interface LigneBonValidee {
  readonly id: string;
  readonly lineKey: string;
  readonly kind: GenreLigneBon;
  readonly reference: string | null;
  readonly designation: string;
  readonly quantity: string | null;
  readonly unit: string | null;
  readonly note: string | null;
  /** Le n° de série d'un matériel livré ou installé ; `null` pour toute autre ligne. */
  readonly serialNumber: string | null;
}

/** Une suite à donner, née à la validation d'un bon. */
export interface SuiteBonValidee {
  readonly id: string;
  readonly kind: GenreSuiteBon;
  readonly comment: string;
  readonly status: EtatSuiteBon;
  readonly resolutionNote: string | null;
  readonly resolvedAt: string | null;
}

/** Un relevé de compteur écrit par la validation du bon. */
export interface ReleveBonValide {
  readonly id: string;
  readonly counterId: string;
  readonly counterLabel: string;
  readonly unit: string | null;
  readonly value: string;
  readonly anomaly: boolean;
  readonly anomalyReason: string | null;
  /** Il a REMPLACÉ un relevé identique déjà saisi à la main (l'ancien reste lisible, « remplacé »). */
  readonly adopte: boolean;
}

/** L'entretien réalisé écrit par la validation du bon. */
export interface EntretienBonValide {
  readonly id: string;
  readonly levelCode: string;
  /** AAAA-MM-JJ. */
  readonly performedOn: string;
  readonly counterValue: string | null;
  /** Il a REMPLACÉ un entretien identique déjà saisi à la main (l'ancien reste lisible, « remplacé »). */
  readonly adopte: boolean;
}

/** Ce que la validation d'un bon a écrit, relu pour l'afficher. */
export interface BonValide {
  readonly technicians: readonly string[];
  readonly lines: readonly LigneBonValidee[];
  readonly followUps: readonly SuiteBonValidee[];
  readonly readings: readonly ReleveBonValide[];
  readonly service: EntretienBonValide | null;
  /** Les contrôles de la nature du bon, tels qu'ils étaient à la saisie, dans l'ordre d'impression. */
  readonly checks: readonly ControleBon[];
}

/** Ce que la validation ÉCRIRA (récapitulatif avant confirmation). */
export interface ApercuValidationBon {
  readonly number: string;
  /** AAAA-MM-JJ. */
  readonly performedOn: string;
  readonly readings: readonly {
    readonly counterId: string;
    readonly counterLabel: string;
    readonly unit: string | null;
    readonly value: string;
    /** CREER : un relevé neuf ; ADOPTER : il remplace un relevé identique déjà saisi. */
    readonly action: 'CREER' | 'ADOPTER';
    readonly anomaly: boolean;
    readonly anomalyReason: string | null;
  }[];
  readonly service: {
    readonly levelCode: string;
    readonly performedOn: string;
    readonly counterValue: string | null;
    /** CREER : un entretien neuf ; ADOPTER : il remplace un entretien identique déjà saisi. */
    readonly action: 'CREER' | 'ADOPTER';
  } | null;
  readonly technicians: number;
  /** Vrai : le bon n’a pas « Contrat annuel / Facturé à la visite » (clôturé sur le téléphone) : la validation exige le choix. */
  readonly billingRequired: boolean;
  readonly lines: number;
  readonly checks: number;
  readonly followUps: number;
  readonly messages: readonly string[];
}

/** Une suite à donner vue de la fiche d'une machine : elle dit de quel bon elle vient. */
export interface SuiteMachine extends SuiteBonValidee {
  readonly interventionId: string;
  readonly interventionNumber: string;
  /** Le jour de l'intervention dont elle est née. */
  readonly performedOn: string | null;
  readonly createdAt: string;
}

export interface ListeSuitesMachine {
  readonly items: readonly SuiteMachine[];
  readonly total: number;
}

// --- La file « Suites à traiter » (lot I2.1) ---------------------------------------------

/** Les critères de la file : tout est facultatif (par défaut, les suites OUVERTES, 50 à la fois). */
export interface FiltreSuitesATraiter {
  readonly q?: string;
  /** États séparés par des virgules : « OPEN,CLOSED ». */
  readonly statuses?: string;
  /** Types séparés par des virgules : « QUOTE_TO_DO,PART_TO_ORDER ». */
  readonly kinds?: string;
  readonly assetId?: string;
  readonly customerId?: string;
  readonly take?: number;
  readonly skip?: number;
}

/**
 * Une suite à donner vue de la file : elle dit de quel bon, de quelle machine et pour qui le travail a été fait (le site et
 * le client sont ceux du bon). Aucun prix ; « résolue par » n'est pas rendu.
 */
export interface SuiteATraiter extends SuiteBonValidee {
  /** Jeton de concurrence : à renvoyer pour écarter ou clore. */
  readonly revision: number;
  /** L'INTERVENTION qui traite cette suite (ADR-0016) : l'ouverte, à défaut la plus récente non annulée ; `null` : aucune. */
  readonly treatedBy: { readonly id: string; readonly number: string; readonly status: string } | null;
  readonly createdAt: string;
  readonly intervention: { readonly id: string; readonly number: string; readonly performedOn: string | null };
  readonly asset: {
    readonly id: string;
    readonly label: string | null;
    readonly model: string | null;
    readonly serialNumber: string | null;
    readonly clientDesignation: string | null;
    readonly archived: boolean;
  };
  readonly site: { readonly id: string; readonly name: string } | null;
  readonly customer: { readonly id: string; readonly name: string } | null;
}

export interface ListeSuitesATraiter {
  readonly items: readonly SuiteATraiter[];
  readonly total: number;
}

// --- Planning (lot PL, ADR-0015 ; interventions depuis ADR-0016) : les types qui restent après le retrait des visites ----------

export type GenreVisite = 'CUSTOMER' | 'WORKSHOP';
/** L'état d'une occurrence de tournée, tel que le serveur le dérive de ses interventions. */
export type StatutOccurrence = 'OPEN' | 'DONE' | 'CANCELLED';

export interface VisiteMachine {
  /** Identifiant de la LIGNE (la machine DE LA VISITE). */
  readonly id: string;
  readonly assetId: string;
  readonly levelKey: string | null;
  readonly label: string | null;
  readonly model: string | null;
  readonly serialNumber: string | null;
  readonly clientDesignation: string | null;
  readonly assetType: string;
  readonly archived: boolean;
}

export interface VisiteTechnicien {
  readonly id: string;
  readonly name: string;
  /** Archivé depuis : il reste affiché sur l'existant, on ne peut plus lui en affecter. */
  readonly archived: boolean;
}

export type GenreAbsence = 'LEAVE' | 'TRAINING' | 'SICK' | 'OTHER';

/** La charge PRÉVUE d'un technicien sur la semaine et sa capacité (calculées par le serveur, jamais par l'écran). */
export interface ChargeTechnicien {
  /** Minutes prévues (visites ouvertes ou faites qui portent une durée). */
  readonly minutes: number;
  /** Visites de la semaine sans durée : elles ne comptent pas dans les minutes. */
  readonly visitesSansDuree: number;
  /** Jours ouvrés non absents × la journée de travail. */
  readonly capaciteMinutes: number;
  /** Jours ouvrés d'absence de la semaine. */
  readonly joursAbsents: number;
}

/** Une absence qui touche la semaine (du jour `from` au jour `to` INCLUS). */
export interface AbsenceSemaine {
  readonly id: string;
  readonly technicianId: string;
  readonly kind: GenreAbsence;
  readonly from: string;
  readonly to: string;
}

export interface Absence extends AbsenceSemaine {
  readonly technicianName: string;
}

export interface LigneHeuresPrevues {
  readonly technicianId: string;
  readonly technicianName: string;
  readonly type: string;
  readonly nature: 'visite' | 'absence';
  readonly minutes: number;
  readonly nombre: number;
  readonly sansDuree: number;
  /** Registre B34 : heures réalisées (pointages, ou saisies au bureau) — trajet, sur site, pause ; travail = les trois. */
  readonly trajetMinutes: number;
  readonly surSiteMinutes: number;
  readonly pauseMinutes: number;
  readonly travailMinutes: number;
}

export interface HeuresPrevues {
  readonly from: string;
  readonly to: string;
  readonly dayMinutes: number;
  readonly lignes: readonly LigneHeuresPrevues[];
}

export interface TechnicienPlanning {
  readonly id: string;
  readonly name: string;
  readonly archived: boolean;
  readonly charge: ChargeTechnicien;
}

// --- L'intervention unique vue depuis le planning (lot U1c, ADR-0016) : aucun prix ------------------------------------

export interface InterventionDeMachine {
  readonly id: string;
  readonly number: string | null;
  readonly natureLabel: string | null;
  readonly prochainJour: string | null;
  readonly windowStart: string | null;
  readonly windowEnd: string | null;
  /** Les techniciens du prochain jour ; vide = équipe à choisir. */
  readonly technicians: readonly { readonly id: string; readonly name: string }[];
  readonly tentative: boolean;
  readonly enRetard: boolean;
}

export interface ListeInterventionsDeMachine {
  readonly today: string;
  readonly items: readonly InterventionDeMachine[];
  readonly total: number;
}

export interface EntreeIntervention {
  readonly kind?: GenreVisite;
  readonly siteId?: string | null;
  readonly natureId?: string | null;
  readonly machines?: readonly { readonly assetId: string; readonly levelKey?: string | null }[];
  readonly title?: string | null;
  readonly description?: string | null;
  readonly customerOrderRef?: string | null;
  readonly tentative?: boolean;
  readonly windowStart?: string | null;
  readonly windowEnd?: string | null;
  readonly jours?: readonly string[];
  readonly technicianIds?: readonly string[];
  readonly dureeMinutes?: number | null;
  /** La suite à donner (retour ou révision à planifier) que l'intervention va traiter : soldée à sa validation. */
  readonly followUpId?: string;
  readonly clientKey?: string;
}

export interface CreationIntervention {
  readonly groupKey: string | null;
  readonly interventions: readonly {
    readonly id: string;
    readonly number: string | null;
    readonly status: 'PLANNED' | 'TO_ASSIGN';
    readonly assetId: string | null;
    readonly isCarrier: boolean;
  }[];
}

/** Ce que rend chaque écriture de jours ou d'équipe : la révision NOUVELLE de l'intervention. */
export interface RevisionIntervention {
  readonly interventionId: string;
  readonly revision: number;
}

export interface InterventionPlanning {
  readonly id: string;
  /** Nul pour une occurrence de tournée pas encore confirmée : on dit « prévue ». */
  readonly number: string | null;
  readonly kind: 'CUSTOMER' | 'WORKSHOP';
  readonly status: string;
  readonly tentative: boolean;
  readonly title: string | null;
  readonly natureLabel: string | null;
  readonly site: { readonly id: string; readonly name: string } | null;
  readonly customerName: string | null;
  /** Nul chez un client = « machine à identifier ». */
  readonly asset: { readonly id: string; readonly label: string | null } | null;
  readonly groupKey: string | null;
  readonly isCarrier: boolean;
  readonly customerOrderRef: string | null;
  readonly windowStart: string | null;
  readonly windowEnd: string | null;
  readonly revision: number;
  /** Créée par un technicien sur son téléphone (registre B33), et non au bureau. */
  readonly creeeSurLeTerrain: boolean;
}

export type ZoneIntervention = 'SEMAINE' | 'A_AFFECTER' | 'MASQUEE' | string;

/** Matin ou après-midi d'un jour d'intervention (lot T0) ; nul = « dans la journée ». */
export type CreneauJour = 'MORNING' | 'AFTERNOON';

export interface CaseSemaine {
  readonly day: string;
  /** Le jour d'intervention de la case (le même pour toute l'équipe ce jour-là). */
  readonly dayId: string;
  /** Matin, après-midi, ou nul (« dans la journée ») : partagé par toute l'équipe du jour. */
  readonly slot: CreneauJour | null;
  /** Heure de rendez-vous « HH:MM », facultative. */
  readonly appointmentTime: string | null;
  /** Ordre choisi à la main dans le créneau. */
  readonly rank: number;
  /** Nul = la ligne « À affecter » : un jour sans équipe. */
  readonly technicianId: string | null;
  readonly isLead: boolean;
  readonly minutesPlanned: number | null;
  readonly intervention: InterventionPlanning;
  readonly zone: ZoneIntervention;
  readonly enRetard: boolean;
  readonly urgence: string | null;
}

export interface SemaineInterventions {
  readonly today: string;
  /** La période lue : une semaine, ou un mois civil (une colonne par jour). Absente d'une réponse d'avant le 2026-10-30 : semaine. */
  readonly vue?: 'semaine' | 'mois';
  readonly from: string;
  readonly to: string;
  readonly jours: readonly string[];
  readonly technicians: readonly TechnicienPlanning[];
  readonly cases: readonly CaseSemaine[];
  readonly absences: readonly AbsenceSemaine[];
  readonly dayMinutes: number;
  readonly compteurs: { readonly aAffecter: number; readonly enRetard: number };
}

export interface LigneAAffecterIntervention {
  readonly intervention: InterventionPlanning;
  readonly echeance: string | null;
  readonly joursRestants: number | null;
  readonly enRetard: boolean;
  readonly urgence: string | null;
}

export interface ListeAAffecterInterventions {
  readonly today: string;
  readonly items: readonly LigneAAffecterIntervention[];
  readonly total: number;
}

export type RaisonSansLigne = 'SANS_MACHINE' | 'SANS_GAMME' | 'GAMME_SANS_VERSION' | 'PALIER_A_CHOISIR' | 'PALIER_INCONNU' | 'AUCUNE_PIECE';

export interface LignePrevue {
  readonly lineKey: string | null;
  readonly kind: 'PART' | 'LABOR' | 'TRAVEL' | 'FLAT_FEE' | 'EQUIPMENT' | 'ACCESSORY';
  readonly reference: string | null;
  readonly designation: string;
  /** Un décimal en texte ; nul = quantité inconnue (« à préciser »). */
  readonly quantite: string | null;
  readonly unite: string | null;
  readonly note: string | null;
  /** Le palier d'où vient la ligne ; `null` = ajoutée à la main. */
  readonly versedFrom: { readonly profileId: string; readonly levelCode: string } | null;
  /** Présent seulement pour une ligne de matériel venue d'un bon : réémis tel quel à l'écriture. */
  readonly serialNumber?: string | null;
}

/** Une ligne de la LISTE COMPLÈTE envoyée à `PATCH /planning/interventions/:id` (clé `lignes`). Jamais un prix. */
export interface LigneSaisieIntervention {
  /** Absent = nouvelle ligne : le serveur assigne l'identifiant. */
  readonly lineKey?: string;
  readonly kind: LignePrevue['kind'];
  readonly reference: string | null;
  readonly designation: string;
  /** Chaîne décimale au POINT (« 2.5 ») ; `null` = quantité inconnue (jamais 1). */
  readonly quantity: string | null;
  readonly unit: string | null;
  readonly note: string | null;
  readonly serialNumber?: string | null;
  readonly versedFrom?: { readonly profileId: string; readonly levelCode: string };
}

/**
 * Ce que `PATCH /planning/interventions/:id` accepte pour CORRIGER une intervention. Une clé absente ne change rien : on n'envoie
 * que ce qui a changé. Les jours et l'équipe sont des listes COMPLÈTES ; la durée est celle de chaque technicien, chaque jour.
 */
export interface ModificationIntervention {
  readonly revision: number;
  readonly natureId?: string;
  /** Atelier seulement. */
  readonly title?: string;
  readonly description?: string | null;
  readonly customerOrderRef?: string | null;
  readonly tentative?: boolean;
  /** L'échéance se modifie en entier : les deux bornes, ou les deux à `null`. */
  readonly windowStart?: string | null;
  readonly windowEnd?: string | null;
  /** Rattache une machine à une intervention « à identifier » (jamais l'inverse, jamais une autre machine). */
  readonly assetId?: string;
  readonly jours?: readonly string[];
  readonly technicianIds?: readonly string[];
  readonly dureeMinutes?: number | null;
  /** Vrai : le déplacement de ce passage n'est pas facturé ; faux : il l'est de nouveau. */
  readonly travelWaived?: boolean;
}

export interface BriefingIntervention {
  readonly intervention: InterventionPlanning & { readonly description: string | null; readonly natureId: string | null };
  readonly plannedLevelKey: string | null;
  /** La tournée d'où vient l'intervention ; `null` hors tournée. Ce que l'intervention en lit n'est jamais recopié. */
  readonly tour: {
    readonly id: string;
    readonly label: string;
    readonly stopped: boolean;
    readonly frequencyMonths: number;
    readonly occurrenceNo: number | null;
    readonly occurrencesTotal: number;
    readonly note: string | null;
    readonly customerOrderRef: string | null;
    readonly durationMinutes: number | null;
  } | null;
  readonly jours: readonly {
    readonly day: string;
    /** Matin, après-midi, ou nul (« dans la journée ») ; heure de rendez-vous facultative (lot T0). */
    readonly slot: CreneauJour | null;
    readonly appointmentTime: string | null;
    readonly members: readonly {
      readonly technicianId: string;
      readonly name: string;
      readonly isLead: boolean;
      readonly minutesPlanned: number | null;
      /** Les heures saisies ce jour-là, et ce qui sera (ou a été) facturé au palier supérieur ; `null` = pas de valeur. */
      readonly minutesEntered: number | null;
      readonly minutesBilled: number | null;
    }[];
  }[];
  /** Ce que le site demande (fiche du site, jamais recopié) ; `null` quand aucune consigne n'est renseignée. */
  readonly consignesSite: { readonly accessNotes: string | null; readonly openingHours: string | null; readonly safetyNotes: string | null } | null;
  /** Derniers bons validés et suites à traiter de la machine ; `null` sans machine. */
  readonly machine: {
    readonly derniersBons: readonly { readonly id: string; readonly number: string | null; readonly performedOn: string | null; readonly natureLabel: string | null }[];
    readonly suites: readonly { readonly id: string; readonly kind: string }[];
  } | null;
  /** Avancement du passage groupé (une intervention par machine) ; `null` hors passage. */
  readonly groupe: {
    readonly valides: number;
    readonly total: number;
    readonly lignes: readonly { readonly id: string; readonly number: string | null; readonly status: string; readonly machineLabel: string | null }[];
  } | null;
  readonly gamme: {
    readonly etat: string;
    readonly gamme: { readonly id: string; readonly code: string; readonly label: string; readonly versionNumber: number | null } | null;
    readonly palier: { readonly code: string; readonly label: string } | null;
    /** Tous les paliers de la version en vigueur, du moins englobant au plus : de quoi choisir. Vide sans gamme. */
    readonly paliers: readonly PalierVisite[];
  } | null;
  readonly lignesPrevues: {
    readonly source: 'SAISIES' | 'GAMME' | 'AUCUNE';
    readonly lignes: readonly LignePrevue[];
    readonly raison: RaisonSansLigne | null;
  };
  /** Registre B34 : les pointages par jour et par technicien (seulement ceux qui ont pointé). */
  readonly pointages: readonly PointagesJour[];
}

// --- Les pièces à préparer (lot PL4) : lues dans la gamme de la machine, au palier choisi, SANS prix ---

export interface PieceVisite {
  /** La référence officielle du constructeur ; `null` quand la ligne n'en porte pas. */
  readonly reference: string | null;
  readonly designation: string;
  /** Un décimal en texte (« 14 », « 2.5 ») ; `null` = quantité inconnue : à préciser, jamais « 1 ». */
  readonly quantite: string | null;
  readonly unite: string | null;
}

export interface PalierVisite {
  readonly levelKey: string;
  readonly code: string;
  readonly label: string;
  readonly rank: number;
  /** Combien de pièces à remplacer ce palier compte. */
  readonly pieces: number;
}

export type EtatPieces = 'OK' | 'SANS_GAMME' | 'GAMME_SANS_VERSION' | 'PALIER_A_CHOISIR' | 'PALIER_INCONNU' | 'AUCUNE_PIECE';

export interface PiecesMachineVisite {
  /** Identifiant de la LIGNE de la visite (la machine DE LA VISITE). */
  readonly lineId: string;
  readonly assetId: string;
  readonly levelKey: string | null;
  readonly etat: EtatPieces;
  readonly gamme: { readonly id: string; readonly code: string; readonly label: string; readonly versionNumber: number | null } | null;
  readonly palier: PalierVisite | null;
  /** Tous les paliers de la version en vigueur, du moins englobant au plus : de quoi choisir. Vide sans gamme. */
  readonly paliers: readonly PalierVisite[];
  /** Les pièces du palier choisi, lues dans la gamme. */
  readonly pieces: readonly PieceVisite[];
}

export interface PieceTotalVisite {
  readonly reference: string | null;
  readonly designation: string;
  readonly unite: string | null;
  /** La somme des quantités CONNUES ; `null` si aucune ne l'est. */
  readonly quantite: string | null;
  /** Au moins une ligne additionnée n'avait pas de quantité : le total est un minimum. */
  readonly quantiteIncomplete: boolean;
  /** Les numéros (à partir de 1) des visites de la fiche qui demandent cette pièce. */
  readonly visites: readonly number[];
}

export interface PreparationVisite {
  readonly ordre: number;
  readonly id: string;
  readonly client: string;
  readonly site: string | null;
  readonly ville: string | null;
  readonly nature: string | null;
  readonly technicians: readonly VisiteTechnicien[];
  readonly durationMinutes: number | null;
  readonly machines: readonly { readonly machine: VisiteMachine; readonly pieces: PiecesMachineVisite }[];
}

export interface Preparation {
  readonly jour: string;
  readonly technicien: { readonly id: string; readonly name: string } | null;
  readonly visites: readonly PreparationVisite[];
  readonly total: readonly PieceTotalVisite[];
  /** Les visites d'atelier du jour : sans machine, donc sans pièces, elles ne figurent pas sur la fiche. */
  readonly visitesAtelier: number;
}

/** Un technicien de la liste d'administration (avec son jeton de concurrence). */
/** Une correction des heures d'un technicien après la validation (lot U2b). */
export interface AjustementHeures {
  readonly id: string;
  /** AAAA-MM-JJ. */
  readonly day: string;
  readonly technicianId: string;
  readonly technicianName: string;
  readonly minutesBefore: number | null;
  readonly minutesAfter: number;
  readonly billedBefore: number | null;
  readonly billedAfter: number;
  readonly billingStep: number;
  readonly reason: string;
  readonly createdAt: string;
}

export interface Technicien {
  readonly id: string;
  readonly name: string;
  readonly archived: boolean;
  readonly revision: number;
  /**
   * L'accès au téléphone (lot T1) : le compte de l'espace relié à ce nom, ou `null`. Le nom du compte seulement (prénom et
   * nom), jamais son adresse électronique. Facultatif à la lecture : un serveur antérieur au lot T1 ne le rend pas.
   */
  readonly acces?: AccesTechnicien | null;
}

/** Le compte relié à un technicien de l'annuaire. */
export interface AccesTechnicien {
  readonly membershipId: string;
  readonly nom: string;
}

/** Un compte qu'on peut relier à un technicien : une adhésion ACTIVE de l'espace, et le technicien qui la porte déjà. */
export interface AccesPossible {
  readonly membershipId: string;
  readonly nom: string;
  /** Le technicien déjà relié à ce compte, ou `null` s'il est libre. */
  readonly relieA: string | null;
}

// --- Tournées (lot PL3, ADR-0015) --------------------------------------------------------------------

export interface OccurrenceTournee {
  readonly no: number;
  readonly date: string;
}

export interface EntreeTournee {
  readonly clientKey?: string;
  readonly label?: string;
  readonly siteId: string;
  readonly natureId: string;
  readonly frequencyMonths: number;
  readonly firstDay: string;
  readonly technicianIds: readonly string[];
  readonly assetIds: readonly string[];
  readonly customerOrderRef?: string;
  readonly note?: string;
  readonly durationMinutes?: number;
  readonly firstVisitNote?: string;
  readonly firstVisitParts?: readonly {
    readonly assetId: string;
    readonly designation: string;
    readonly quantity: string;
    readonly reference?: string;
    readonly unit?: string;
  }[];
}

export interface ModificationTournee {
  readonly revision: number;
  readonly label?: string | null;
  readonly customerOrderRef?: string | null;
  readonly note?: string | null;
  readonly durationMinutes?: number | null;
  /** Machines à ajouter (toutes du site de la tournée) : une intervention par occurrence à venir. */
  readonly addAssetIds?: readonly string[];
  /** Machines à retirer : leurs interventions à venir sont annulées. */
  readonly removeAssetIds?: readonly string[];
  /** La liste COMPLÈTE des techniciens habituels ; absente = inchangée. */
  readonly technicianIds?: readonly string[];
  /** Vrai : aucun déplacement facturé pour les interventions de la tournée (compris dans le forfait). */
  readonly travelWaived?: boolean;
}

/** La réponse d'une modification : la tournée relue et ce que le changement a fait aux occurrences à venir. */
export interface TourneeModifiee extends Tournee {
  readonly machinesAjoutees: number;
  readonly machinesRetirees: number;
  readonly interventionsAnnulees: number;
  readonly interventionsCreees: number;
  readonly interventionsAffectees: number;
}

export interface Tournee {
  readonly id: string;
  readonly label: string;
  readonly siteId: string;
  readonly siteName: string;
  readonly customerName: string;
  readonly natureId: string;
  readonly natureLabel: string;
  readonly frequencyMonths: number;
  readonly firstDay: string;
  readonly customerOrderRef: string | null;
  readonly note: string | null;
  readonly durationMinutes: number | null;
  /** Vrai : aucun déplacement facturé pour les interventions de la tournée. */
  readonly travelWaived: boolean;
  readonly status: 'ACTIVE' | 'STOPPED';
  readonly revision: number;
  readonly technicians: readonly { readonly id: string; readonly name: string }[];
  readonly assets: readonly { readonly assetId: string; readonly label: string }[];
  readonly occurrences: readonly {
    /** L'intervention porteuse de l'occurrence (la première du passage) : celle que l'écran ouvre. */
    readonly interventionId: string;
    /** Combien d'interventions l'occurrence compte : une par machine. */
    readonly interventions: number;
    readonly no: number;
    readonly plannedDate: string | null;
    readonly status: StatutOccurrence;
    readonly technicianNames: readonly string[];
  }[];
}

/** La réponse d'un changement de rythme : l'ancienne tournée (arrêtée), la nouvelle, et les interventions à venir annulées. */
export interface TourneeRythmeChange {
  readonly ancienne: Tournee;
  readonly nouvelle: Tournee;
  readonly interventionsAnnulees: number;
}

export interface TourneeArretee extends Tournee {
  /** Les interventions « à affecter » ou « planifiées » d'aujourd'hui ou plus tard que l'arrêt a annulées. */
  readonly interventionsAnnulees: number;
}
